import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { differenceInMinutes, differenceInSeconds } from 'date-fns';
import { CreditCard, Phone, Eye, EyeOff, Coins, X, Flag, Plus, AlertTriangle, ChevronDown, ChevronUp, Loader2, Copy } from 'lucide-react';
import { cpApi } from '../api/api';
import ConfirmTakeWidget from '../components/ConfirmTakeWidget';
import ConfirmCancelWidget from '../components/ConfirmCancelWidget';
import { ReceiptViewWidget } from '../components/ReceiptViewWidget';
import { useTransactionsFilters } from '../context/TransactionsFilterContext';
import { isAutoRefreshEnabled, getAutoRefreshInterval, getIssuedRequestIds, toggleIssuedRequest } from '../utils/preferences';

interface AirEXRequest {
  id: number;
  order_id: string;
  amount: number;
  amount_usd?: number;
  exchange_rate?: number;
  payed_amount?: number;
  payed_amount_counterparty?: number;
  bank_name?: string;
  direction_code?: string;
  card_holder?: string;
  card_number?: string;
  sbp_phone?: string;
  status: string;
  created_at?: string;
  work_started_at?: string | null;
  work_finished_at?: string | null;
  /** Крайний срок заявки AirEX (как у трейдера в airex-platform) */
  deadline_at?: string | null;
  max_matches?: number;
  match_tolerance?: number;
  use_match_tolerance?: boolean;
  tolerance_direction?: string;
  closed_manually?: boolean;
  currency_iso?: string;
  [key: string]: unknown;
}

interface MatchedRequest {
  id: number;
  wt_request_id: number | null;
  airex_request_id: number;
  amount: number;
  amount_usd: number;
  exchange_rate: number;
  status: string;
  created_at: string;
  updated_at: string;
  receipt_file_path?: string | null;
  wallet_id?: number | null;
  wallet_name?: string | null;
  initiator_name?: string | null;
  counterparty_id?: number | null;
}

/** Скрывать реквизиты, если заявка закрыта/возврат или нет своего/командного мэтча in_progress. */
const shouldMaskRequestRequisites = (
  request: AirEXRequest,
  requestMatches: MatchedRequest[],
  currentUserId: number | undefined,
  isResponsible: boolean,
  teamMemberIds: Set<number>,
): boolean => {
  if (request.status === 'closed' || request.status === 'returned') return true;
  const hasMyInProgress = requestMatches.some(
    (m) => m.status === 'in_progress' && m.counterparty_id === currentUserId,
  );
  const hasTeamInProgress =
    isResponsible &&
    requestMatches.some(
      (m) => m.status === 'in_progress' && m.counterparty_id != null && teamMemberIds.has(m.counterparty_id),
    );
  return !(hasMyInProgress || hasTeamInProgress);
};

const MASKED_REQUISITE_TEXT = '•••• ••••';
const MASKED_HOLDER_TEXT = '••••••';

const AVAILABLE_COL_DEFAULT_WIDTHS = [48, 55, 80, 180, 80];
const STORAGE_MY_COL_WIDTHS = 'airex_cp_requests_my_col_widths_v1';
const STORAGE_AVAILABLE_COL_WIDTHS = 'airex_cp_requests_available_col_widths_v1';
const STORAGE_AVAILABLE_PANEL_WIDTHS = 'airex_cp_requests_available_panel_widths_v1';

const readWidthsFromStorage = (key: string, expectedLength: number): number[] | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== expectedLength) return null;
    const normalized = parsed.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0);
    return normalized.length === expectedLength ? normalized : null;
  } catch {
    return null;
  }
};

const emitToast = (message: string, type: 'success' | 'error' = 'success') => {
  window.dispatchEvent(new CustomEvent('showToast', { detail: { message, type } }));
};

const CP_RECEIPT_ERROR_MESSAGES: Record<string, string> = {
  counterparty_receipt_not_original:
    'PDF-чек не прошёл проверку оригинальности. Загрузите другой файл или обратитесь в поддержку.',
  counterparty_receipt_struct_queued:
    'Данные чека не совпадают с данными заявками.\nОжидайте обработку вашей заявки.',
};

const mapCpReceiptApiError = (raw: string): string => {
  for (const [code, message] of Object.entries(CP_RECEIPT_ERROR_MESSAGES)) {
    if (raw.includes(code)) return message;
  }
  return raw;
};

/** Соответствие файла чека выплаты настройке команды (только PDF vs PDF+скрины). */
const payoutReceiptFileAllowed = (file: File, allowImages: boolean): boolean => {
  if (allowImages) {
    return (
      file.type === 'application/pdf' ||
      file.type === 'image/png' ||
      file.type === 'image/jpeg' ||
      file.type === 'image/jpg'
    );
  }
  return file.type === 'application/pdf';
};

const getApiError = (err: unknown): string => {
  if (typeof err === 'string') return mapCpReceiptApiError(err);
  const e = err as {
    response?: { data?: string | { error?: string } };
    message?: string;
    code?: string;
  };
  if (e?.code === 'ERR_NETWORK' || /INTERNET_DISCONNECTED/i.test(e?.message || '')) {
    return 'Нет соединения с сервером. Проверьте интернет и отключите Offline в DevTools (Network Throttling).';
  }
  const data = e?.response?.data;
  if (typeof data === 'string') return mapCpReceiptApiError(data);
  if (data && typeof data === 'object') {
    const msg = (data as { error?: string }).error || 'Ошибка';
    return mapCpReceiptApiError(msg);
  }
  return mapCpReceiptApiError(e?.message || 'Ошибка');
};

const maskOrderId = (orderId: string | null | undefined): string => {
  if (!orderId) return '';
  if (orderId.length <= 8) return orderId;
  return orderId.slice(0, 4) + '....' + orderId.slice(-4);
};

const formatBankShort = (bankName: string | null | undefined): string => {
  if (!bankName) return '';
  return bankName.length <= 10 ? bankName : `${bankName.slice(0, 5)}...${bankName.slice(-4)}`;
};

const isC2CMethod = (code?: string) => code === 'tbank_c2c' || code === 'interbank_c2c';
const isSBPMethod = (code?: string) => code === 'tbank_sbp' || code === 'interbank_sbp';
type RequisiteType = 'phone' | 'card';

const normalizeDigits = (value: string | null | undefined): string => String(value || '').replace(/\D/g, '');
const isRuPhoneLike = (value: string | null | undefined): boolean => {
  const d = normalizeDigits(value);
  return (d.length === 11 && (d.startsWith('7') || d.startsWith('8'))) || (d.length === 10 && d.startsWith('9'));
};

/** Для СБП: отображаем номер телефона с пробелами (7 901 746 37 13, 901 746 37 13, +7 901 746 37 13). Копирование — без пробелов. */
const formatPhoneWithSpaces = (value: string | null | undefined): string => {
  if (!value || typeof value !== 'string') return value ?? '';
  const trimmed = value.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10 && digits.startsWith('9')) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  }
  if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8')) {
    const rest = `${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
    return digits[0] === '7' && hasPlus ? `+7 ${rest}` : `${digits[0]} ${rest}`;
  }
  return value;
};

const getRequestPhoneValue = (request: AirEXRequest): string => {
  const explicitPhone = String(request.sbp_phone || '').trim();
  if (explicitPhone) return explicitPhone;
  const cardAsPhone = String(request.card_number || '').trim();
  if (isSBPMethod(request.direction_code) && isRuPhoneLike(cardAsPhone)) return cardAsPhone;
  return '';
};

const getRequestCardValue = (request: AirEXRequest): string => {
  const raw = String(request.card_number || '').trim();
  if (!raw) return '';
  const phone = getRequestPhoneValue(request);
  return phone && normalizeDigits(phone) === normalizeDigits(raw) ? '' : raw;
};

const getSelectedRequisiteType = (
  request: AirEXRequest,
  selectedType?: RequisiteType
): RequisiteType => {
  if (selectedType) return selectedType;
  const hasPhone = !!getRequestPhoneValue(request);
  const hasCard = !!getRequestCardValue(request);
  if (hasPhone && hasCard) return 'phone';
  return hasPhone ? 'phone' : 'card';
};

const getDisplayRequisite = (
  request: AirEXRequest,
  selectedType?: RequisiteType
): string => {
  const type = getSelectedRequisiteType(request, selectedType);
  if (type === 'phone') {
    const phone = getRequestPhoneValue(request);
    return phone ? formatPhoneWithSpaces(phone) : '—';
  }
  const card = getRequestCardValue(request);
  return card ? card : '—';
};

const RequisiteTypeSwitch = ({
  request,
  selectedType,
  onSelect,
  className = '',
}: {
  request: AirEXRequest;
  selectedType: RequisiteType;
  onSelect: (type: RequisiteType) => void;
  className?: string;
}) => {
  const hasPhone = !!getRequestPhoneValue(request);
  const hasCard = !!getRequestCardValue(request);
  if (!hasPhone || !hasCard) return null;

  const buttonClass = (type: RequisiteType) =>
    `inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
      selectedType === type
        ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300'
        : 'text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10'
    }`;

  return (
    <span className={`inline-flex shrink-0 items-center gap-1 ${className}`}>
      <button
        type="button"
        title="СБП"
        className={buttonClass('phone')}
        onClick={(e) => {
          e.stopPropagation();
          onSelect('phone');
        }}
      >
        <Phone className="h-4 w-4" />
      </button>
      <button
        type="button"
        title="Карта"
        className={buttonClass('card')}
        onClick={(e) => {
          e.stopPropagation();
          onSelect('card');
        }}
      >
        <CreditCard className="h-4 w-4" />
      </button>
    </span>
  );
};

const buildRequestClipboardText = (request: AirEXRequest): string => {
  return `Заявка\nID ваш: ${request.order_id || '-'}\nID наш: ${request.id}\nСумма: ${Number(request.amount ?? 0).toLocaleString('ru-RU')} ₽`;
};

export default function RequestsPage() {
  const txFilters = useTransactionsFilters();
  const team = txFilters?.team ?? null;
  const [availableRequests, setAvailableRequests] = useState<AirEXRequest[]>([]);
  const [myRequests, setMyRequests] = useState<AirEXRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showConfirmTake, setShowConfirmTake] = useState(false);
  const [selectedTake, setSelectedTake] = useState<AirEXRequest | null>(null);
  /** Сумма мэтча из виджета «Подобрать» (частичный take); иначе null = полный остаток */
  const [takeIntentRub, setTakeIntentRub] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [myRequestsCurrentPage, setMyRequestsCurrentPage] = useState(1);
  const [myRequestsTotalCount, setMyRequestsTotalCount] = useState(0);
  const [mobileSection, setMobileSection] = useState<'available' | 'mine'>('available');
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(isAutoRefreshEnabled());
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(getAutoRefreshInterval());

  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    return { from: yesterday, to: today };
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [matchedRequests, setMatchedRequests] = useState<Map<number, MatchedRequest[]>>(new Map());
  const [availableMatchedRequests, setAvailableMatchedRequests] = useState<Map<number, MatchedRequest[]>>(new Map());
  const [expandedRequests, setExpandedRequests] = useState<Set<number>>(new Set());
  const [expandedMatchRows, setExpandedMatchRows] = useState<Set<number>>(new Set());
  const [loadingMatchesRequestId, setLoadingMatchesRequestId] = useState<number | null>(null);
  const [, setMatchingSettings] = useState<{
    overpayment_rules?: Array<{ min: number; max: number; percent: number; max_overpayment_amount: number }>;
    match_tolerance?: number;
    use_match_tolerance?: boolean;
    tolerance_direction?: string;
  }>({ overpayment_rules: [] });
  const [currentUser, setCurrentUser] = useState<{ id: number; name?: string; can_cancel?: boolean } | null>(null);
  /** Маркер «выдал заявку» — персональный флаг */
  const [issuedRequestIds, setIssuedRequestIds] = useState<Set<number>>(() => getIssuedRequestIds());
  const [requisitesViewByRequestId, setRequisitesViewByRequestId] = useState<Record<number, RequisiteType>>({});

  useEffect(() => {
    const handler = () => setIssuedRequestIds(getIssuedRequestIds());
    window.addEventListener('issuedRequestChanged', handler);
    return () => window.removeEventListener('issuedRequestChanged', handler);
  }, []);

  const [, setWallets] = useState<Array<{ id: number; name: string; calculated_balance: number; type: string }>>([]);
  const [payoutWidgetOpen, setPayoutWidgetOpen] = useState<number | null>(null);
  const [payoutWidgetMode, setPayoutWidgetMode] = useState<'payout' | 'issue'>('payout');
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutReceiptFile, setPayoutReceiptFile] = useState<File | null>(null);
  // Остаток при частичной выплате всегда закрепляется за исполнителем (серверная защита).
  const [isCreatingPayout, setIsCreatingPayout] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState<{ wtRequestId: number; airexOrderId?: string } | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [receiptWidget, setReceiptWidget] = useState<{ wtRequestId: number; receiptFilePath?: string | null } | null>(null);
  const isResponsible = !!(team && currentUser && team.responsible_counterparty_id === currentUser.id);
  const bodyTableRef = useRef<HTMLTableElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [colWidths, setColWidths] = useState<number[]>(() => readWidthsFromStorage(STORAGE_MY_COL_WIDTHS, 11) ?? []);
  const [isManualColResize, setIsManualColResize] = useState(() => (readWidthsFromStorage(STORAGE_MY_COL_WIDTHS, 11)?.length ?? 0) === 11);
  const resizingColRef = useRef<{ index: number; startX: number; startWidth: number } | null>(null);
  const [availableColWidths, setAvailableColWidths] = useState<number[]>(
    () => readWidthsFromStorage(STORAGE_AVAILABLE_COL_WIDTHS, 5) ?? AVAILABLE_COL_DEFAULT_WIDTHS
  );
  const resizingAvailableColRef = useRef<{ index: number; startX: number; startWidth: number } | null>(null);
  const [availablePanelWidths, setAvailablePanelWidths] = useState<number[]>(
    () => readWidthsFromStorage(STORAGE_AVAILABLE_PANEL_WIDTHS, 3) ?? []
  );
  const resizingAvailablePanelRef = useRef<{ index: number; startX: number; startWidth: number } | null>(null);
  const [myRatesByCurrency, setMyRatesByCurrency] = useState<Record<string, { min: number; max: number; rate: number }[]>>({});
  const [myCommissionsByCurrency, setMyCommissionsByCurrency] = useState<Record<string, { min: number; max: number; percent: number }[]>>({});
  const [closureRatesByRequestId, setClosureRatesByRequestId] = useState<Record<number, number>>({});
  /** Сохранённое состояние виджета выплат по requestId при закрытии (без лишнего) */
  const lastPayoutStateByRequestIdRef = useRef<Record<number, { mode: 'payout' | 'issue'; selectedMatchId: number | null; amount: string }>>({});

  const getRateForAmount = useCallback((amount: number, rates: { min: number; max: number; rate: number }[]) => {
    if (!rates?.length) return null;
    const match = rates
      .filter((r) => amount >= (r.min ?? 0) && ((r.max ?? 0) === 0 || amount <= (r.max ?? 0)))
      .sort((a, b) => (b.min ?? 0) - (a.min ?? 0))[0];
    return match ? match.rate : rates[0]?.rate ?? null;
  }, []);

  /** Процент контрагента по градации для суммы (для курса от заявки: exchange_rate × (1 + percent/100)). */
  const getPercentForAmount = useCallback((amount: number, commissions: { min: number; max: number; percent: number }[]) => {
    if (!commissions?.length) return null;
    const match = commissions
      .filter((c) => amount >= (c.min ?? 0) && ((c.max ?? 0) === 0 || amount <= (c.max ?? 0)))
      .sort((a, b) => (b.min ?? 0) - (a.min ?? 0))[0];
    return match ? match.percent : commissions[0]?.percent ?? null;
  }, []);

  const requestCurrencyISO = useCallback((request: AirEXRequest) => {
    const iso = String(request.currency_iso || 'RUB').trim().toUpperCase();
    return iso || 'RUB';
  }, []);

  const loadClosureRatesForRequests = useCallback(async (requests: AirEXRequest[], counterpartyId?: number | null) => {
    if (!counterpartyId || !requests.length) return;
    try {
      const res = await cpApi.getAirexClosureRatesBatch(requests.map((x) => x.id), counterpartyId);
      setClosureRatesByRequestId((prev) => ({ ...prev, ...res }));
    } catch {
      // не блокируем страницу: fallback rate рассчитывается локально
    }
  }, []);

  const formatDateLocal = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getWorkTimeColor = (minutes: number): string => {
    if (minutes < 30) return 'text-green-600 dark:text-green-400';
    if (minutes < 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  useEffect(() => {
    let cancelled = false;
    cpApi.getMyCurrentRates()
      .then((r) => {
        if (cancelled) return;
        const commissionsBy: Record<string, { min: number; max: number; percent: number }[]> = {};
        const ratesBy: Record<string, { min: number; max: number; rate: number }[]> = {};
        const cc = r?.currency_commissions && typeof r.currency_commissions === 'object' ? r.currency_commissions : {};
        const cr = r?.currency_rates && typeof r.currency_rates === 'object' ? r.currency_rates : {};
        for (const [isoRaw, rules] of Object.entries(cc)) {
          const iso = String(isoRaw || '').toUpperCase();
          if (iso && Array.isArray(rules)) commissionsBy[iso] = rules;
        }
        for (const [isoRaw, row] of Object.entries(cr)) {
          const iso = String(isoRaw || '').toUpperCase();
          if (!iso || !row) continue;
          if (Array.isArray(row.calculated_commissions)) ratesBy[iso] = row.calculated_commissions;
          if (!commissionsBy[iso] && Array.isArray(row.commissions)) commissionsBy[iso] = row.commissions;
        }
        if (!commissionsBy.RUB && Array.isArray(r?.commissions)) commissionsBy.RUB = r.commissions;
        if (!ratesBy.RUB && Array.isArray(r?.calculated_commissions)) ratesBy.RUB = r.calculated_commissions;
        setMyCommissionsByCurrency(commissionsBy);
        setMyRatesByCurrency(ratesBy);
      })
      .catch(() => { if (!cancelled) { setMyRatesByCurrency({}); setMyCommissionsByCurrency({}); } });
    return () => { cancelled = true; };
  }, []);

  const isTailRequest = useCallback((request: AirEXRequest): boolean => {
    if (request.status !== 'work' && request.status !== 'in_progress') return false;
    const reqMatches = matchedRequests.get(request.id) || availableMatchedRequests.get(request.id) || [];
    const usedMatches = reqMatches.filter((m) => m.status === 'closed' || m.status === 'in_progress').length;
    const maxMatches = request.max_matches || 0;
    if (maxMatches <= 0) return false;
    const remainingMatches = maxMatches - usedMatches;
    const hasOnlyOneMatchLeft = remainingMatches === 1;
    const hasPartialPayment = (request.payed_amount || 0) > 0 && (request.payed_amount || 0) < request.amount;
    return hasOnlyOneMatchLeft && hasPartialPayment;
  }, [matchedRequests, availableMatchedRequests]);

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'work':
        return 'bg-amber-50 text-amber-800 ring-1 ring-amber-200/70 dark:bg-amber-500/15 dark:text-amber-200 dark:ring-amber-500/30';
      case 'in_progress':
        return 'bg-sky-50 text-sky-800 ring-1 ring-sky-200/70 dark:bg-sky-500/15 dark:text-sky-200 dark:ring-sky-500/30';
      case 'closed':
        return 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/70 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/30';
      case 'problem':
        return 'bg-orange-50 text-orange-800 ring-1 ring-orange-200/70 dark:bg-orange-500/15 dark:text-orange-200 dark:ring-orange-500/30';
      case 'canceled':
        return 'bg-rose-50 text-rose-800 ring-1 ring-rose-200/70 dark:bg-rose-500/15 dark:text-rose-200 dark:ring-rose-500/30';
      case 'pending_canceled':
        return 'bg-violet-50 text-violet-800 ring-1 ring-violet-200/70 dark:bg-violet-500/15 dark:text-violet-200 dark:ring-violet-500/30';
      case 'returned':
        return 'bg-orange-50 text-orange-800 ring-1 ring-orange-200/70 dark:bg-orange-500/15 dark:text-orange-200 dark:ring-orange-500/30';
      case 'open':
        return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200/70 dark:bg-slate-500/15 dark:text-slate-200 dark:ring-slate-500/30';
      default:
        return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200/70 dark:bg-slate-500/15 dark:text-slate-200 dark:ring-slate-500/30';
    }
  };

  const getStatusRowGradient = (status: string): string => {
    const s = status.toLowerCase();
    if (s === 'work' || s === 'in_progress') return 'bg-gradient-to-r from-amber-50/50 via-amber-100/40 to-amber-50/50 dark:from-amber-500/10 dark:via-amber-500/5 dark:to-amber-500/10';
    if (s === 'closed') return 'bg-gradient-to-r from-emerald-50/50 via-emerald-100/40 to-emerald-50/50 dark:from-emerald-500/10 dark:via-emerald-500/5 dark:to-emerald-500/10';
    if (s === 'problem' || s === 'canceled' || s === 'returned') return 'bg-gradient-to-r from-rose-50/50 via-orange-50/40 to-rose-50/50 dark:from-rose-500/10 dark:via-orange-500/5 dark:to-rose-500/10';
    if (s === 'pending_canceled') return 'bg-gradient-to-r from-violet-50/50 via-violet-100/40 to-violet-50/50 dark:from-violet-500/10 dark:via-violet-500/5 dark:to-violet-500/10';
    return 'bg-gradient-to-r from-slate-50/50 via-slate-100/40 to-slate-50/50 dark:from-slate-500/10 dark:via-slate-500/5 dark:to-slate-500/10';
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'work': return 'В работе';
      case 'in_progress': return 'В процессе';
      case 'closed': return 'Оплачено';
      case 'problem': return 'Проблема';
      case 'canceled': return 'Отменена';
      case 'pending_canceled': return 'К закрытию';
      case 'returned': return 'Возвращена';
      case 'open': return 'Открыта';
      default: return status;
    }
  };

  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(num);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return '-';
      return formatInTimeZone(d, 'Europe/Moscow', 'dd.MM.yyyy');
    } catch {
      return '-';
    }
  };

  const formatTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return '-';
      return formatInTimeZone(d, 'Europe/Moscow', 'HH:mm');
    } catch {
      return '-';
    }
  };

  const calculateWorkTimeFull = (createdAt: string, _ws: string | null | undefined, workFinishedAt: string | null | undefined): { text: string; color: string } | string => {
    try {
      const start = new Date(createdAt);
      const end = workFinishedAt ? new Date(workFinishedAt) : new Date();
      const moscowStart = formatInTimeZone(start, 'Europe/Moscow', "yyyy-MM-dd'T'HH:mm:ssXXX");
      const moscowEnd = formatInTimeZone(end, 'Europe/Moscow', "yyyy-MM-dd'T'HH:mm:ssXXX");
      const s = new Date(moscowStart);
      const e = new Date(moscowEnd);
      if (isNaN(s.getTime()) || isNaN(e.getTime())) return '-';
      const minutes = differenceInMinutes(e, s);
      if (minutes < 0) return '-';
      let text = '';
      if (minutes < 60) {
        const sec = differenceInSeconds(e, s) % 60;
        text = `${Math.floor(minutes)} мин ${sec} с`;
      } else if (minutes < 1440) {
        text = `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`;
      } else {
        text = `${Math.floor(minutes / 1440)} д ${Math.floor((minutes % 1440) / 60)} ч`;
      }
      return { text, color: getWorkTimeColor(minutes) };
    } catch {
      return '-';
    }
  };

  /** Остаток до дедлайна AirEX (логика как у трейдера в TraderAirexRequestsPage). */
  const formatDeadlineRemaining = (deadlineAt: string | null | undefined, now: Date): string => {
    if (!deadlineAt) return '—';
    try {
      const end = new Date(deadlineAt).getTime();
      const diff = Math.max(0, end - now.getTime());
      if (diff === 0) return 'Истекло';
      const min = Math.floor(diff / 60000);
      const h = Math.floor(min / 60);
      if (h >= 1) return `${h} ч ${min % 60} мин`;
      return `${min} мин`;
    } catch {
      return '—';
    }
  };

  const formatPoolDeadlineRemaining = (
    deadlineAt: string | null | undefined,
    now: Date,
  ): { text: string; color: string } => {
    if (!deadlineAt) {
      return { text: 'Нет дедлайна', color: 'text-gray-500 dark:text-gray-400' };
    }
    try {
      const end = new Date(deadlineAt).getTime();
      if (isNaN(end)) {
        return { text: 'Нет дедлайна', color: 'text-gray-500 dark:text-gray-400' };
      }
      const diff = end - now.getTime();
      if (diff <= 0) {
        return { text: 'Истекло', color: 'text-rose-600 dark:text-rose-400 font-medium' };
      }
      const totalSec = Math.floor(diff / 1000);
      const minutes = Math.floor(totalSec / 60);
      let text = '';
      if (minutes < 60) {
        text = `${minutes} мин ${totalSec % 60} с`;
      } else if (minutes < 1440) {
        text = `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`;
      } else {
        text = `${Math.floor(minutes / 1440)} д ${Math.floor((minutes % 1440) / 60)} ч`;
      }
      const color =
        minutes < 5
          ? 'text-rose-600 dark:text-rose-400 font-medium'
          : minutes < 30
            ? 'text-yellow-600 dark:text-yellow-400'
            : 'text-green-600 dark:text-green-400';
      return { text, color };
    } catch {
      return { text: 'Нет дедлайна', color: 'text-gray-500 dark:text-gray-400' };
    }
  };

  /** Длительность от startAt до сейчас (для таймера «В работе» у себя/подчинённого). */
  const formatDurationFrom = (startAt: string): { text: string; color: string } | string => {
    try {
      const start = new Date(startAt);
      const end = new Date(currentTime);
      if (isNaN(start.getTime())) return '-';
      const minutes = differenceInMinutes(end, start);
      if (minutes < 0) return '-';
      let text = '';
      if (minutes < 60) {
        const sec = differenceInSeconds(end, start) % 60;
        text = `${Math.floor(minutes)} мин ${sec} с`;
      } else if (minutes < 1440) {
        text = `${Math.floor(minutes / 60)} ч ${minutes % 60} мин`;
      } else {
        text = `${Math.floor(minutes / 1440)} д ${Math.floor((minutes % 1440) / 60)} ч`;
      }
      return { text, color: getWorkTimeColor(minutes) };
    } catch {
      return '-';
    }
  };

  const memberFilter = txFilters?.filters?.memberFilter ?? 'all';
  const metricsCounterpartyId = (isResponsible && memberFilter !== 'all') ? (parseInt(memberFilter, 10) || currentUser?.id) : currentUser?.id;

  const teamMemberIds = React.useMemo(() => {
    const ids = new Set<number>();
    team?.members?.forEach((m) => { if (m.counterparty_id) ids.add(m.counterparty_id); });
    if (team?.responsible_counterparty_id) ids.add(team.responsible_counterparty_id);
    return ids;
  }, [team]);

  /** Мэтч «в работе», принадлежащий текущему пользователю или участнику команды (подчинённому). */
  const getMyOrTeamInProgressMatch = useCallback((requestId: number): MatchedRequest | null => {
    const matches = matchedRequests.get(requestId) || [];
    const inProgress = matches.filter((m) => m.status === 'in_progress');
    const myId = currentUser?.id;
    if (!myId) return inProgress[0] ?? null;
    const mine = inProgress.find((m) => m.counterparty_id === myId);
    if (mine) return mine;
    const subordinate = inProgress.find((m) => m.counterparty_id != null && teamMemberIds.has(m.counterparty_id));
    return subordinate ?? null;
  }, [matchedRequests, currentUser?.id, teamMemberIds]);

  const calculateMetrics = useCallback(
    (request: AirEXRequest) => {
      const matches = matchedRequests.get(request.id) || availableMatchedRequests.get(request.id) || [];
      const remaining = request.amount - (request.payed_amount || 0) - (request.payed_amount_counterparty || 0);
      const myMatches = metricsCounterpartyId ? matches.filter((m) => m.counterparty_id === metricsCounterpartyId) : [];
      const myIssued = myMatches.filter((m) => m.status === 'in_progress').reduce((sum, m) => sum + m.amount, 0);
      const myPaid = myMatches.filter((m) => m.status === 'closed').reduce((sum, m) => sum + m.amount, 0);
      const issued = metricsCounterpartyId ? myIssued : (request.payed_amount_counterparty || 0);
      const paid = metricsCounterpartyId ? myPaid : (request.payed_amount || 0);
      const paidMatches = metricsCounterpartyId ? myMatches.filter((m) => m.status === 'closed') : matches.filter((m) => m.status === 'closed');
      const usdtGiven = paidMatches.reduce((sum, m) => sum + (m.amount_usd ?? 0), 0);
      const totalMatched = paidMatches.reduce((sum, m) => sum + m.amount, 0);
      const orderBookRate = usdtGiven > 0 ? totalMatched / usdtGiven : 0;
      const historicalRateFromMatches = (() => {
        if (orderBookRate > 0) return orderBookRate;
        const withRate = paidMatches.filter((m) => (m.exchange_rate ?? 0) > 0);
        if (withRate.length === 0) return 0;
        const usdt = withRate.reduce((sum, m) => sum + m.amount / (m.exchange_rate as number), 0);
        return usdt > 0 ? withRate.reduce((sum, m) => sum + m.amount, 0) / usdt : 0;
      })();
      // Live-курс — только для открытых заявок; закрытые мэтчи показываем по сохранённым фактам.
      const backendClosureRate = closureRatesByRequestId[request.id] ?? 0;
      const percent = getPercentForAmount(request.amount, myCommissionsByCurrency[requestCurrencyISO(request)] || []);
      const fallbackRate =
        (request.exchange_rate != null && request.exchange_rate > 0 && percent != null)
          ? request.exchange_rate * (1 + percent / 100)
          : (getRateForAmount(request.amount, myRatesByCurrency[requestCurrencyISO(request)] || []) ?? 0);
      const liveRate = backendClosureRate > 0 ? backendClosureRate : fallbackRate;
      const calculatedRate = historicalRateFromMatches > 0 ? historicalRateFromMatches : liveRate;
      // Получили: для контрагента — сумма amount_usd по нашим закрытым мэтчам (как в админке), иначе — по курсу заявки
      const usdtReceived = metricsCounterpartyId
        ? paidMatches.reduce((sum, m) => sum + (m.amount_usd ?? 0), 0)
        : (calculatedRate > 0 ? request.amount / calculatedRate : 0);
      const profit = usdtGiven === 0 ? 0 : usdtReceived - usdtGiven;
      const profitPercent = usdtGiven === 0 ? 0 : (usdtReceived > 0 ? (profit / usdtReceived) * 100 : 0);
      const usedMatches = metricsCounterpartyId ? myMatches.filter((m) => m.status === 'closed' || m.status === 'in_progress').length : matches.filter((m) => m.status === 'closed' || m.status === 'in_progress').length;
      // По заявке всего (как у админа): тек. / макс. в колонке «Мэтчи»
      const totalUsedMatches = matches.filter((m) => m.status === 'closed' || m.status === 'in_progress').length;
      return {
        remaining,
        issued,
        paid,
        calculatedRate,
        usdtReceived,
        usdtGiven,
        orderBookRate,
        profit,
        profitPercent,
        usedMatches,
        totalUsedMatches,
      };
    },
    [matchedRequests, availableMatchedRequests, closureRatesByRequestId, myRatesByCurrency, myCommissionsByCurrency, getRateForAmount, getPercentForAmount, requestCurrencyISO, metricsCounterpartyId]
  );

  const calculateMetricsForDetails = useCallback(
    (request: AirEXRequest) => {
      const matches = matchedRequests.get(request.id) || [];
      const remaining = request.amount - (request.payed_amount || 0) - (request.payed_amount_counterparty || 0);
      const useTeamMetrics = isResponsible && teamMemberIds.size > 0;
      const relevantMatches = useTeamMetrics
        ? matches.filter((m) => m.counterparty_id != null && teamMemberIds.has(m.counterparty_id))
        : metricsCounterpartyId
          ? matches.filter((m) => m.counterparty_id === metricsCounterpartyId)
          : [];
      const issued = relevantMatches.filter((m) => m.status === 'in_progress').reduce((sum, m) => sum + m.amount, 0);
      const paid = relevantMatches.filter((m) => m.status === 'closed').reduce((sum, m) => sum + m.amount, 0);
      return { remaining, issued, paid };
    },
    [matchedRequests, isResponsible, teamMemberIds, metricsCounterpartyId]
  );

  const loadAvailableRequests = useCallback(async () => {
    try {
      const data = await cpApi.getAvailableAirexRequests();
      const list = Array.isArray(data) ? (data as AirEXRequest[]) : [];
      setAvailableRequests(list);
      const matchesMap = new Map<number, MatchedRequest[]>();
      const matchesById = await cpApi.getAirexRequestMatchesBatch(list.map((r) => r.id));
      for (const r of list) {
        matchesMap.set(r.id, (matchesById[r.id] ?? []) as MatchedRequest[]);
      }
      setAvailableMatchedRequests(matchesMap);
      await loadClosureRatesForRequests(list, metricsCounterpartyId ?? currentUser?.id);
    } catch (err) {
      setError(getApiError(err));
      emitToast(`Ошибка загрузки доступных: ${getApiError(err)}`, 'error');
    }
  }, [loadClosureRatesForRequests, metricsCounterpartyId, currentUser?.id]);

  const loadMyRequests = useCallback(async () => {
    try {
      const from = dateRange.from || new Date();
      const to = dateRange.to || new Date();
      const { data, total_count } = await cpApi.getMyAirexRequests({
        dateFrom: formatDateLocal(from),
        dateTo: formatDateLocal(to),
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery.trim() || undefined,
        limit: 20,
        offset: (myRequestsCurrentPage - 1) * 20,
      });
      const pageRequests = Array.isArray(data) ? (data as AirEXRequest[]) : [];
      setMyRequests(pageRequests);
      const needsLiveClosureRate = (r: AirEXRequest) =>
        r.status !== 'closed' && r.status !== 'returned' && r.status !== 'canceled' && r.status !== 'problem';
      await loadClosureRatesForRequests(
        pageRequests.filter(needsLiveClosureRate),
        metricsCounterpartyId ?? currentUser?.id
      );
      setMyRequestsTotalCount(total_count ?? 0);
      const matchesMap = new Map<number, MatchedRequest[]>();
      const matchesById = await cpApi.getAirexRequestMatchesBatch(pageRequests.map((r) => r.id));
      for (const r of pageRequests) {
        matchesMap.set(r.id, (matchesById[r.id] ?? []) as MatchedRequest[]);
      }
      setMatchedRequests(matchesMap);
    } catch (err) {
      setError(getApiError(err));
      emitToast(`Ошибка загрузки своих заявок: ${getApiError(err)}`, 'error');
    }
  }, [dateRange, statusFilter, searchQuery, myRequestsCurrentPage, loadClosureRatesForRequests, metricsCounterpartyId, currentUser?.id]);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else (window as unknown as { setAirexTableUpdating?: (v: boolean) => void }).setAirexTableUpdating?.(true);
    setError(null);
    try {
      await Promise.all([loadAvailableRequests(), loadMyRequests()]);
    } finally {
      if (!silent) setLoading(false);
      else (window as unknown as { setAirexTableUpdating?: (v: boolean) => void }).setAirexTableUpdating?.(false);
    }
  }, [loadAvailableRequests, loadMyRequests]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  useEffect(() => {
    const h = () => loadData(true);
    window.addEventListener('refreshRequests', h);
    return () => window.removeEventListener('refreshRequests', h);
  }, [loadData]);

  useEffect(() => {
    const hStatus = (e: Event) => setStatusFilter((e as CustomEvent).detail?.status ?? 'all');
    const hDate = (e: Event) => setDateRange((e as CustomEvent).detail?.range ?? { from: null, to: null });
    const hSearch = (e: Event) => setSearchQuery((e as CustomEvent).detail?.search ?? '');
    window.addEventListener('airexStatusFilterChange', hStatus);
    window.addEventListener('airexDateRangeChange', hDate);
    window.addEventListener('searchChange', hSearch);
    return () => {
      window.removeEventListener('airexStatusFilterChange', hStatus);
      window.removeEventListener('airexDateRangeChange', hDate);
      window.removeEventListener('searchChange', hSearch);
    };
  }, []);

  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ enabled?: boolean; interval?: number }>).detail;
      if (d) {
        setAutoRefreshEnabled(d.enabled ?? isAutoRefreshEnabled());
        setAutoRefreshInterval(d.interval ?? getAutoRefreshInterval());
      }
    };
    window.addEventListener('autoRefreshChanged', h);
    return () => window.removeEventListener('autoRefreshChanged', h);
  }, []);

  useEffect(() => {
    setMyRequestsCurrentPage(1);
  }, [dateRange, statusFilter, searchQuery, txFilters?.filters?.memberFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (!loading) loadMyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadMyRequests is stable, loading triggers refetch
  }, [dateRange, statusFilter, searchQuery, myRequestsCurrentPage, loading]);

  useEffect(() => {
    if (!autoRefreshEnabled) return;
    const id = setInterval(() => {
      if (showConfirmTake || payoutWidgetOpen !== null) return;
      (window as unknown as { setAirexTableUpdating?: (v: boolean) => void }).setAirexTableUpdating?.(true);
      loadData(true).finally(() => (window as unknown as { setAirexTableUpdating?: (v: boolean) => void }).setAirexTableUpdating?.(false));
    }, autoRefreshInterval * 1000);
    return () => clearInterval(id);
  }, [autoRefreshEnabled, autoRefreshInterval, loadData, showConfirmTake, payoutWidgetOpen]);

  useEffect(() => {
    const load = async () => {
      try {
        const [profile, settings, walletsResp] = await Promise.all([
          cpApi.getProfile(),
          cpApi.getMatchSettings().catch(() => ({ overpayment_rules: [] })),
          cpApi.getWallets({ limit: 1000 }),
        ]);
        setCurrentUser({ id: profile.id, name: profile.name, can_cancel: profile.can_cancel ?? false });
        setMatchingSettings({
          overpayment_rules: settings.overpayment_rules || [],
          match_tolerance: settings.match_tolerance,
          use_match_tolerance: settings.use_match_tolerance,
          tolerance_direction: settings.tolerance_direction,
        });
        setWallets(walletsResp.wallets || []);
      } catch {
        // ignore
      }
    };
    load();
  }, []);

  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ requestId: number; mode?: 'payout' | 'issue'; matchId?: number; matchAmount?: number }>).detail;
      if (d?.requestId) {
        setPayoutWidgetOpen(d.requestId);
        setPayoutWidgetMode(d.mode || 'payout');
        setSelectedMatchId(d.matchId ?? null);
        setPayoutAmount(d.matchAmount != null ? Number(d.matchAmount).toFixed(2) : '');
        setPayoutReceiptFile(null);
        // остаток всегда закрепляется автоматически
      }
    };
    window.addEventListener('openPayoutWidget', h);
    return () => window.removeEventListener('openPayoutWidget', h);
  }, []);

  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ request: AirEXRequest; match_amount?: number }>).detail;
      if (d?.request) {
        setSelectedTake(d.request);
        const m = d.match_amount;
        setTakeIntentRub(m != null && Number.isFinite(m) && m > 0 ? m : null);
        setShowConfirmTake(true);
      }
    };
    window.addEventListener('openConfirmTake', h);
    return () => window.removeEventListener('openConfirmTake', h);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handleTakeClick = (r: AirEXRequest) => {
    setSelectedTake(r);
    setTakeIntentRub(null);
    setShowConfirmTake(true);
  };

  const handleTakeConfirm = async () => {
    if (!selectedTake) return;
    if (actionLoading === selectedTake.id) return;
    setActionLoading(selectedTake.id);
    try {
      const expectedRemainingRub =
        selectedTake.amount -
        (selectedTake.payed_amount || 0) -
        (selectedTake.payed_amount_counterparty || 0);
      await cpApi.takeAirexRequest(selectedTake.id, {
        amount: takeIntentRub != null ? takeIntentRub : undefined,
        expectedRemainingRub,
      });
      const amtHint = takeIntentRub != null ? ` на ${takeIntentRub.toLocaleString('ru-RU')} ₽` : '';
      emitToast(`Заявка #${selectedTake.order_id} взята в работу${amtHint}`);
      setShowConfirmTake(false);
      setSelectedTake(null);
      setTakeIntentRub(null);
      await loadData();
    } catch (err) {
      emitToast(getApiError(err), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectWTRequest = async () => {
    if (!cancelConfirm) return;
    if (isCanceling) return;
    setIsCanceling(true);
    try {
      await cpApi.rejectWTRequest(cancelConfirm.wtRequestId);
      emitToast(`WT-заявка #${cancelConfirm.wtRequestId} отменена`);
      setCancelConfirm(null);
      setPayoutWidgetOpen(null);
      // остаток всегда закрепляется автоматически
      await loadData();
    } catch (err) {
      emitToast(getApiError(err), 'error');
    } finally {
      setIsCanceling(false);
    }
  };

  const REQUESTS_PER_PAGE = 15;
  const searchLower = searchQuery.toLowerCase().trim();
  const searchDigits = searchLower.replace(/\D/g, '');
  const filteredAvailable = searchLower
    ? availableRequests.filter((r) => {
        const id = String(r.id || '').toLowerCase();
        const orderId = String(r.order_id || '').toLowerCase();
        const bankName = String(r.bank_name || '').toLowerCase();
        const cardHolder = String(r.card_holder || '').toLowerCase();
        const direction = String(r.direction_code || '').toLowerCase();
        const cardNumberRaw = String(r.card_number || '');
        const cardNumber = cardNumberRaw.toLowerCase();
        const normalizedCardNumber = cardNumberRaw.replace(/\D/g, '');
        const amount = String(r.amount ?? '').toLowerCase();

        return (
          id.includes(searchLower) ||
          orderId.includes(searchLower) ||
          bankName.includes(searchLower) ||
          cardHolder.includes(searchLower) ||
          direction.includes(searchLower) ||
          amount.includes(searchLower) ||
          cardNumber.includes(searchLower) ||
          (searchDigits.length > 0 && normalizedCardNumber.includes(searchDigits))
        );
      })
    : availableRequests;

  const totalPages = Math.ceil(filteredAvailable.length / REQUESTS_PER_PAGE);
  const startIdx = (currentPage - 1) * REQUESTS_PER_PAGE;
  const paginated = filteredAvailable.slice(startIdx, startIdx + REQUESTS_PER_PAGE);
  const col1 = paginated.slice(0, 5);
  const col2 = paginated.slice(5, 10);
  const col3 = paginated.slice(10, 15);

  const MY_PER_PAGE = 20;
  let filteredMyRequests = searchLower
    ? myRequests.filter(
        (r) =>
          String(r.order_id || '').toLowerCase().includes(searchLower) ||
          String(r.bank_name || '').toLowerCase().includes(searchLower) ||
          String(r.card_holder || '').toLowerCase().includes(searchLower)
      )
    : myRequests;
  if (isResponsible && memberFilter !== 'all') {
    const memberId = parseInt(memberFilter, 10);
    if (!isNaN(memberId)) {
      filteredMyRequests = filteredMyRequests.filter((r) => {
        const matches = matchedRequests.get(r.id) || [];
        return matches.some((m) => m.counterparty_id === memberId);
      });
    }
  }
  const myTotalPages = Math.max(1, Math.ceil(myRequestsTotalCount / MY_PER_PAGE));

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('paginationUpdate', {
        detail: { currentPage: myRequestsCurrentPage, totalPages: myTotalPages, showPagination: myTotalPages > 1 },
      })
    );
  }, [myRequestsCurrentPage, myTotalPages]);

  useEffect(() => {
    const h = (e: Event) => {
      const p = (e as CustomEvent<{ page: number }>).detail?.page;
      if (typeof p === 'number' && p >= 1) setMyRequestsCurrentPage(Math.min(p, myTotalPages));
    };
    window.addEventListener('pageChange', h);
    return () => window.removeEventListener('pageChange', h);
  }, [myTotalPages]);

  // Синхронизация ширины колонок — те же правила, что у трейдера
  useLayoutEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const measureColumns = () => {
      if (isManualColResize && colWidths.length === 11) return;
      if (filteredMyRequests.length === 0) {
        // Не сбрасываем сохраненные ширины при временно пустой выборке (фильтры/переключение вкладок/обновление).
        return;
      }
      const table = bodyTableRef.current;
      if (!table) return;

      const rows = table.querySelectorAll('tbody tr');
      const allNormalRows = Array.from(rows).filter((row) => {
        const cells = row.querySelectorAll('td');
        return cells.length === 13;
      }) as HTMLTableRowElement[];

      if (allNormalRows.length === 0) {
        const ths = table.querySelectorAll('thead th');
        if (ths.length >= 11) {
          const widths = Array.from(ths).slice(0, 11).map((th) => Math.max(56, (th as HTMLElement).offsetWidth));
          setColWidths(widths);
        }
        return;
      }

      const widths: number[] = [];
      for (let colIndex = 0; colIndex < 11; colIndex++) {
        let maxWidth = 0;
        for (const row of allNormalRows) {
          const cells = row.querySelectorAll('td');
          if (cells[colIndex]) {
            const el = cells[colIndex] as HTMLElement;
            const width = el.offsetWidth;
            if (width > maxWidth) maxWidth = width;
          }
        }
        widths.push(maxWidth);
      }
      // Столбец «Мэтчи» — узкий; сэкономленное место отдаём столбцу «Держ.»
      if (widths.length > 8) {
        const matchesWidth = widths[4];
        widths[4] = Math.min(widths[4], 56);
        let saved = Math.max(0, matchesWidth - widths[4]);
        // Столбец «Статус» — ограничиваем ширину, место отдаём «Держ.»
        const statusWidth = widths[3];
        widths[3] = Math.min(widths[3], 100);
        saved += Math.max(0, statusWidth - widths[3]);
        // Столбец «Банк | Метод» — не растягивать, макс. 180px, сэкономленное — «Держ.»
        const bankWidth = widths[8];
        widths[8] = Math.min(widths[8], 180);
        saved += Math.max(0, bankWidth - widths[8]);
        widths[7] += saved; // Держ.
      }
      setColWidths(widths);
    };

    timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        measureColumns();
        setTimeout(measureColumns, 100);
      });
    }, 100);

    const table = bodyTableRef.current;
    if (!table) {
      if (timeoutId) clearTimeout(timeoutId);
      return;
    }

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => setTimeout(measureColumns, 10));
      });
      resizeObserver.observe(table);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [filteredMyRequests.length, filteredMyRequests, isManualColResize, colWidths.length]);

  useEffect(() => {
    if (colWidths.length === 11) {
      localStorage.setItem(STORAGE_MY_COL_WIDTHS, JSON.stringify(colWidths));
    }
  }, [colWidths]);

  useEffect(() => {
    if (availableColWidths.length === 5) {
      localStorage.setItem(STORAGE_AVAILABLE_COL_WIDTHS, JSON.stringify(availableColWidths));
    }
  }, [availableColWidths]);

  useEffect(() => {
    if (availablePanelWidths.length === 3) {
      localStorage.setItem(STORAGE_AVAILABLE_PANEL_WIDTHS, JSON.stringify(availablePanelWidths));
    }
  }, [availablePanelWidths]);

  const getMinColWidth = (index: number) => {
    if (index === 4) return 56; // Мэтчи
    if (index === 3) return 90; // Статус
    if (index === 0) return 70; // Действия
    return 80;
  };

  const startHeaderResize = (index: number, e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();

    let initialWidths = colWidths;
    if (initialWidths.length !== 11) {
      const table = bodyTableRef.current;
      if (!table) return;
      const ths = table.querySelectorAll('thead th');
      if (ths.length < 11) return;
      initialWidths = Array.from(ths).slice(0, 11).map((th) => Math.max(56, (th as HTMLElement).offsetWidth));
      setColWidths(initialWidths);
    }

    setIsManualColResize(true);
    resizingColRef.current = {
      index,
      startX: e.clientX,
      startWidth: initialWidths[index],
    };

    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      const current = resizingColRef.current;
      if (!current) return;
      const delta = ev.clientX - current.startX;
      const minWidth = getMinColWidth(current.index);
      const nextWidth = Math.max(minWidth, Math.round(current.startWidth + delta));
      setColWidths((prev) => {
        if (prev.length !== 11) return prev;
        const next = [...prev];
        next[current.index] = nextWidth;
        return next;
      });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      resizingColRef.current = null;
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const startAvailableHeaderResize = (index: number, e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const minByIndex = [28, 36, 44, 90, 28];
    const maxByIndex = [90, 120, 170, 320, 200];
    const minWidth = minByIndex[index] ?? 60;

    resizingAvailableColRef.current = {
      index,
      startX: e.clientX,
      startWidth: availableColWidths[index],
    };

    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      const current = resizingAvailableColRef.current;
      if (!current) return;
      const delta = ev.clientX - current.startX;
      const maxWidth = maxByIndex[current.index] ?? 380;
      const nextWidth = Math.min(maxWidth, Math.max(minWidth, Math.round(current.startWidth + delta)));
      setAvailableColWidths((prev) => {
        const next = [...prev];
        next[current.index] = nextWidth;
        return next;
      });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      resizingAvailableColRef.current = null;
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const startAvailablePanelResize = (index: number, e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const minWidth = 180;
    const maxWidth = Math.max(260, window.innerWidth - 40);

    let initial = availablePanelWidths;
    if (initial.length !== 3) {
      const wrap = (e.currentTarget as HTMLElement).closest('[data-available-grid-wrap]') as HTMLElement | null;
      if (!wrap) return;
      const total = wrap.clientWidth;
      const gap = 8 * 2; // gap-2 -> 8px, между 3 колонками два промежутка
      const each = Math.max(minWidth, Math.floor((total - gap) / 3));
      initial = [each, each, each];
      setAvailablePanelWidths(initial);
    }

    resizingAvailablePanelRef.current = {
      index,
      startX: e.clientX,
      startWidth: initial[index],
    };

    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      const current = resizingAvailablePanelRef.current;
      if (!current) return;
      const delta = ev.clientX - current.startX;
      const nextWidth = Math.min(maxWidth, Math.max(minWidth, Math.round(current.startWidth + delta)));
      setAvailablePanelWidths((prev) => {
        const next = prev.length === 3 ? [...prev] : [current.startWidth, current.startWidth, current.startWidth];
        next[current.index] = nextWidth;
        return next;
      });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      resizingAvailablePanelRef.current = null;
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleCreatePayout = async () => {
    if (!payoutWidgetOpen) return;
    const request = myRequests.find((r) => r.id === payoutWidgetOpen);
    if (!request) return;
    const requestMatches = matchedRequests.get(payoutWidgetOpen) || [];
    const existingMatch = payoutWidgetMode === 'payout' ? requestMatches.find((m) => m.status === 'in_progress' && m.id === selectedMatchId) : null;
    if (payoutWidgetMode === 'payout' && !existingMatch) {
      emitToast('Для выплаты необходим существующий мэтч в статусе in_progress', 'error');
      return;
    }
    if (!payoutAmount) {
      emitToast('Укажите сумму', 'error');
      return;
    }
    const amount = parseFloat(payoutAmount);
    const creditCounterpartyId =
      (payoutWidgetMode === 'payout' ? existingMatch?.counterparty_id : null) ?? currentUser?.id;
    let exchangeRate = 0;
    if (creditCounterpartyId && creditCounterpartyId > 0) {
      try {
        exchangeRate = await cpApi.getAirexClosureRate(request.id, creditCounterpartyId);
      } catch {
        exchangeRate = 0;
      }
    }
    // Fallback на старую фронтовую формулу, если backend-курс не удалось получить.
    if (!exchangeRate || exchangeRate <= 0) {
      const percent = getPercentForAmount(request.amount, myCommissionsByCurrency[requestCurrencyISO(request)] || []);
      exchangeRate =
        (request.exchange_rate != null && request.exchange_rate > 0 && percent != null)
          ? request.exchange_rate * (1 + percent / 100)
          : (getRateForAmount(request.amount, myRatesByCurrency[requestCurrencyISO(request)] || []) ?? 0);
    }
    if (!exchangeRate || exchangeRate <= 0) {
      emitToast('Курс по градации не найден. Проверьте настройки курсов.', 'error');
      return;
    }
    if (payoutWidgetMode === 'payout' && !payoutReceiptFile) {
      emitToast('Загрузите чек для выплаты', 'error');
      return;
    }
    const allowReceiptImagesTeam = team?.allow_receipt_pdf_upload === true;
    if (payoutWidgetMode === 'payout' && payoutReceiptFile && !payoutReceiptFileAllowed(payoutReceiptFile, allowReceiptImagesTeam)) {
      emitToast(
        allowReceiptImagesTeam
          ? 'Допустимы только PDF, PNG или JPEG.'
          : 'Для вашей команды разрешён только PDF. Включите скрины в настройках команды (админка) или загрузите PDF.',
        'error'
      );
      return;
    }
    setIsCreatingPayout(true);
    try {
      const formData = new FormData();
      formData.append('amount', payoutAmount);
      // Контрагент не использует кошельки — передаём 0, бэкенд обработает по балансу команды
      formData.append('wallet_id', '0');
      formData.append('platform', 'counterparty');
      // Для platform=counterparty бэкенд требует credit_counterparty_id.
      // При закрытии выплаты за участника команды — начисляем именно ему (по match.counterparty_id),
      // иначе используем текущего пользователя.
      if (creditCounterpartyId && creditCounterpartyId > 0) {
        formData.append('credit_counterparty_id', String(creditCounterpartyId));
      }
      if (payoutWidgetMode === 'payout') {
        formData.append('exchange_rate', exchangeRate.toFixed(2));
        if (payoutReceiptFile) formData.append('receipt', payoutReceiptFile);
        if (existingMatch) {
          formData.append('match_id', existingMatch.id.toString());
          const shouldKeepRemainder = amount < existingMatch.amount - 0.0001;
          formData.append('keep_remainder', shouldKeepRemainder ? 'true' : 'false');
        }
      } else {
        formData.append('exchange_rate', exchangeRate.toFixed(2));
      }
      if (payoutWidgetMode !== 'payout') formData.append('should_close', 'false');
      await cpApi.createManualMatch(payoutWidgetOpen, formData);
      emitToast(
        payoutWidgetMode === 'payout'
          ? `Выплата: #${request.order_id} сумма ${formatNumber(amount)} ₽`
          : `Выдать: #${request.order_id} сумма ${formatNumber(amount)} ₽`,
        'success'
      );
      await loadMyRequests();
      // Явно обновляем мэтчи по этой заявке, чтобы при повторном открытии «Выплаты» список был актуальным
      try {
        const freshMatches = (await cpApi.getAirexRequestMatches(payoutWidgetOpen)) as MatchedRequest[];
        setMatchedRequests((prev) => {
          const next = new Map(prev);
          next.set(payoutWidgetOpen, freshMatches);
          return next;
        });
      } catch {
        // при ошибке список уже обновлён через loadMyRequests
      }
      setPayoutWidgetOpen(null);
      setSelectedMatchId(null);
      setPayoutAmount('');
      setPayoutReceiptFile(null);
      // остаток всегда закрепляется автоматически
    } catch (err) {
      emitToast(`Ошибка: ${getApiError(err)}`, 'error');
    } finally {
      setIsCreatingPayout(false);
    }
  };

  const openPayoutWidgetForRequest = async (
    request: AirEXRequest,
    remainingAmount: number,
  ) => {
    if (payoutWidgetOpen === request.id) return;
    setPayoutWidgetOpen(request.id);
    try {
      const matches = await cpApi.getAirexRequestMatches(request.id) as MatchedRequest[];
      setMatchedRequests((prev) => {
        const next = new Map(prev);
        next.set(request.id, matches);
        return next;
      });
      const inProgress = isResponsible
        ? matches.filter((m) => m.status === 'in_progress' && (m.counterparty_id === currentUser?.id || (m.counterparty_id != null && teamMemberIds.has(m.counterparty_id))))
        : matches.filter((m) => m.status === 'in_progress' && m.counterparty_id === currentUser?.id);
      const saved = lastPayoutStateByRequestIdRef.current[request.id];
      if (saved) {
        setPayoutWidgetMode(saved.mode);
        setSelectedMatchId(saved.selectedMatchId);
        setPayoutAmount(saved.amount);
      } else if (inProgress.length > 0) {
        const m = inProgress[0];
        setPayoutWidgetMode('payout');
        setSelectedMatchId(m.id);
        setPayoutAmount(m.amount.toFixed(2));
      } else {
        setPayoutWidgetMode('issue');
        setSelectedMatchId(null);
        setPayoutAmount(remainingAmount.toFixed(2));
      }
      setPayoutReceiptFile(null);
      // остаток всегда закрепляется автоматически
    } catch {
      setPayoutWidgetMode('issue');
      setSelectedMatchId(null);
      setPayoutAmount(remainingAmount.toFixed(2));
      setPayoutReceiptFile(null);
      // остаток всегда закрепляется автоматически
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500 dark:text-gray-400">Загрузка...</div>
      </div>
    );
  }

  return (
    <div
      className="p-2 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden"
      style={{ height: 'calc(100dvh - var(--airex-header-height, 80px))' }}
    >
      <div className="md:hidden mb-2">
        <div className="grid grid-cols-2 gap-1 rounded-xl border border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-white/5 p-1">
          <button
            type="button"
            onClick={() => setMobileSection('available')}
            className={`px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
              mobileSection === 'available'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
            }`}
          >
            Доступные суммы
          </button>
          <button
            type="button"
            onClick={() => setMobileSection('mine')}
            className={`px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
              mobileSection === 'mine'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
            }`}
          >
            Мои заявки
          </button>
        </div>
      </div>

      {/* Верхний блок - Доступные заявки */}
      <div className={`flex-shrink-0 rounded-2xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm p-3 ${mobileSection === 'available' ? 'block' : 'hidden'} md:block`}>
        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs dark:bg-red-950/35 dark:border-red-900/50 dark:text-red-200">
            {error}
          </div>
        )}
        {filteredAvailable.length === 0 ? (
          <div className="text-gray-500 dark:text-gray-400 text-center py-8">Нет доступных заявок</div>
        ) : (
          <>
            <div className="md:hidden space-y-2">
              {paginated.map((request) => {
                const remaining =
                  request.amount - (request.payed_amount || 0) - (request.payed_amount_counterparty || 0);
                const workTime = formatPoolDeadlineRemaining(request.deadline_at, currentTime);
                const wt = workTime.text;
                const wc = workTime.color;
                const metrics = calculateMetrics(request);
                const maxMatches = request.max_matches ?? 0;
                const remainingMatches = maxMatches > 0 ? Math.max(0, maxMatches - (metrics.totalUsedMatches ?? 0)) : 0;
                return (
                  <div
                    key={`mobile-available-${request.id}`}
                    className="rounded-xl border border-gray-200 dark:border-gray-700/60 p-2.5 bg-white/80 dark:bg-white/5"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(request.order_id).then(() => emitToast('ID скопирован', 'success')).catch(() => emitToast('Не удалось скопировать', 'error'));
                        }}
                        className="text-xs font-semibold text-blue-600 dark:text-blue-300 underline truncate"
                      >
                        #{maskOrderId(request.order_id)}
                      </button>
                      <span className={`text-[11px] font-medium ${wc}`}>{wt}</span>
                    </div>
                    <div className="mt-1 text-sm font-semibold text-blue-700 dark:text-blue-400">
                      {remaining.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₽
                    </div>
                    <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-300">
                      <span>{formatBankShort(request.bank_name) || '—'}</span>
                      <span className="mx-1">·</span>
                      <span className="inline-flex items-center gap-1 align-middle">
                        {!!getRequestPhoneValue(request) && <span title="СБП"><Phone className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /></span>}
                        {!!getRequestCardValue(request) && <span title="Карта"><CreditCard className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /></span>}
                        {!getRequestPhoneValue(request) && !getRequestCardValue(request) && <span>—</span>}
                      </span>
                    </div>
                    {maxMatches > 0 && (
                      <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                        Мэтчей осталось: {remainingMatches}
                      </div>
                    )}
                    <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      Курс: {metrics.calculatedRate != null && metrics.calculatedRate > 0 ? formatNumber(metrics.calculatedRate) : '—'}
                    </div>
                    <button
                      onClick={() => handleTakeClick(request)}
                      disabled={actionLoading === request.id}
                      className="mt-2 w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === request.id ? 'Загрузка...' : 'Взять'}
                    </button>
                  </div>
                );
              })}
            </div>
            {/* Легенда "Доступные суммы" с ручным ресайзом заголовков */}
            <div className="hidden md:block mb-2 overflow-x-auto available-desktop-scrollbar">
              <div
                data-available-grid-wrap
                className="grid gap-2"
                style={{
                  gridTemplateColumns:
                    availablePanelWidths.length === 3
                      ? availablePanelWidths.map((w) => `${w}px`).join(' ')
                      : 'repeat(3, minmax(0, 1fr))',
                }}
              >
                {[0, 1, 2].map((legendIdx) => (
                  <div key={legendIdx} className="relative border border-gray-200 dark:border-gray-700/60 rounded px-1.5 py-1 text-xs bg-purple-50/60 dark:bg-white/5">
                    <div className="grid items-center text-gray-600 dark:text-gray-300 [&>div]:overflow-hidden" style={{ gridTemplateColumns: availableColWidths.map((w) => `${w}px`).join(' ') }}>
                      <div className="relative w-full font-medium select-none">
                        Дейст.
                        <span onMouseDown={(e) => startAvailableHeaderResize(0, e)} className="absolute top-0 right-0 h-full w-4 cursor-col-resize z-20 after:content-[''] after:absolute after:right-1 after:top-1/2 after:-translate-y-1/2 after:h-4 after:w-px after:bg-gray-400/70" />
                      </div>
                      <div className="relative border-l border-gray-300 dark:border-gray-600 pl-2 font-medium select-none">
                        ID
                        <span onMouseDown={(e) => startAvailableHeaderResize(1, e)} className="absolute top-0 right-0 h-full w-4 cursor-col-resize z-20 after:content-[''] after:absolute after:right-1 after:top-1/2 after:-translate-y-1/2 after:h-4 after:w-px after:bg-gray-400/70" />
                      </div>
                      <div className="relative border-l border-gray-300 pl-2 font-medium text-blue-600 dark:text-blue-400 select-none">
                        Сумма
                        <span onMouseDown={(e) => startAvailableHeaderResize(2, e)} className="absolute top-0 right-0 h-full w-4 cursor-col-resize z-20 after:content-[''] after:absolute after:right-1 after:top-1/2 after:-translate-y-1/2 after:h-4 after:w-px after:bg-gray-400/70" />
                      </div>
                      <div className="relative border-l border-gray-300 dark:border-gray-600 pl-2 truncate min-w-0 text-gray-700 dark:text-gray-300 select-none">
                        Банк | Метод | Мэтч | Курс
                        <span onMouseDown={(e) => startAvailableHeaderResize(3, e)} className="absolute top-0 right-0 h-full w-4 cursor-col-resize z-20 after:content-[''] after:absolute after:right-1 after:top-1/2 after:-translate-y-1/2 after:h-4 after:w-px after:bg-gray-400/70" />
                      </div>
                      <div className="relative border-l border-gray-300 dark:border-gray-600 pl-2 font-medium select-none">
                        Осталось
                        <span onMouseDown={(e) => startAvailableHeaderResize(4, e)} className="absolute top-0 right-0 h-full w-4 cursor-col-resize z-20 after:content-[''] after:absolute after:right-1 after:top-1/2 after:-translate-y-1/2 after:h-4 after:w-px after:bg-gray-400/70" />
                      </div>
                    </div>
                    <span
                      onMouseDown={(e) => startAvailablePanelResize(legendIdx, e)}
                      className="absolute top-0 -right-2 h-full w-5 cursor-col-resize z-30"
                      title="Тянуть влево/вправо — ширина блока (правый край)"
                    />
                    <span className="pointer-events-none absolute top-1/2 right-0 -translate-y-1/2 h-5 w-[2px] bg-blue-400/70 rounded-full" />
                  </div>
                ))}
              </div>
            </div>
            <div
              className="hidden md:grid gap-2 overflow-x-auto available-desktop-scrollbar"
              style={{
                gridTemplateColumns:
                  availablePanelWidths.length === 3
                    ? availablePanelWidths.map((w) => `${w}px`).join(' ')
                    : 'repeat(3, minmax(0, 1fr))',
              }}
            >
              {[col1, col2, col3].map((column, colIdx) => (
                <div key={colIdx} className="space-y-0.5">
                  {column.map((request) => {
                    const remaining =
                      request.amount - (request.payed_amount || 0) - (request.payed_amount_counterparty || 0);
                    const workTime = formatPoolDeadlineRemaining(request.deadline_at, currentTime);
                    const wt = workTime.text;
                    const wc = workTime.color;
                    const maxMatches = request.max_matches ?? 0;
                    const isTail = isTailRequest(request);
                    const metrics = calculateMetrics(request);
                    const remainingMatches = maxMatches > 0 ? Math.max(0, maxMatches - (metrics.totalUsedMatches ?? 0)) : 0;
                    return (
                      <div
                        key={request.id}
                        className="border border-gray-200 dark:border-gray-700/60 rounded px-1.5 py-1 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-xs"
                      >
                        <div className="grid items-center [&>*]:overflow-hidden" style={{ gridTemplateColumns: availableColWidths.map((w) => `${w}px`).join(' ') }}>
                          <button
                            onClick={() => handleTakeClick(request)}
                            disabled={actionLoading === request.id}
                            className="w-full px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-center"
                          >
                            {actionLoading === request.id ? '...' : 'Взять'}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(request.order_id).then(() => emitToast('ID скопирован', 'success')).catch(() => emitToast('Не удалось скопировать', 'error'));
                            }}
                            className="border-l border-gray-300 dark:border-gray-600 pl-2 font-medium truncate dark:text-gray-100 text-gray-700 dark:text-gray-200 underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 text-left w-full bg-transparent py-0 appearance-none"
                            title={`${request.order_id} (клик — скопировать)`}
                          >
                            #{maskOrderId(request.order_id)}
                          </button>
                          <div className="border-l border-gray-300 pl-2 font-medium text-blue-600 dark:text-blue-400 truncate">
                            {remaining.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₽
                          </div>
                          <div className="border-l border-gray-300 dark:border-gray-600 pl-2 text-gray-700 dark:text-gray-200 truncate min-w-0 flex items-center gap-1">
                            <span className="truncate min-w-0">{formatBankShort(request.bank_name) || '—'}</span>
                            <span className="shrink-0 text-gray-400 dark:text-gray-500">|</span>
                            {!!getRequestPhoneValue(request) && (
                              <span title="СБП"><Phone className="shrink-0 w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /></span>
                            )}
                            {!!getRequestCardValue(request) && (
                              <span title="Карта"><CreditCard className="shrink-0 w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /></span>
                            )}
                            {!getRequestPhoneValue(request) && !getRequestCardValue(request) && (
                              <span className="text-gray-400 dark:text-gray-500 text-[10px] shrink-0">—</span>
                            )}
                            {maxMatches > 0 && (
                              <>
                                <span className="shrink-0 text-gray-400 dark:text-gray-500">|</span>
                                <span className="shrink-0 text-gray-500 dark:text-gray-400 text-[10px] tabular-nums" title={`Осталось мэтчей: ${remainingMatches} (из ${maxMatches})`}>{remainingMatches}</span>
                              </>
                            )}
                            <span className="shrink-0 text-gray-400 dark:text-gray-500">|</span>
                            <span className="shrink-0 font-semibold text-blue-700 dark:text-blue-400 text-[10px] tabular-nums" title="Курс с наценкой контрагента (как в Мои заявки)">
                              {metrics.calculatedRate != null && metrics.calculatedRate > 0 ? formatNumber(metrics.calculatedRate) : '—'}
                            </span>
                          </div>
                          <div className={`border-l border-gray-300 dark:border-gray-600 pl-2 font-medium truncate flex items-center gap-1 min-w-0 tabular-nums ${wc}`}>
                            <span className="truncate">{wt}</span>
                            {isTail && <span className="shrink-0 text-sky-600 font-semibold dark:text-sky-400">Хвост</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Назад
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                        currentPage === page ? 'bg-blue-600 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Вперед
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Нижний блок - Мои заявки (как у трейдера) */}
      <div className={`flex-shrink-0 ${mobileSection === 'mine' ? 'flex' : 'hidden'} md:flex flex-col rounded-2xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-sm w-full mt-2`}>
        {filteredMyRequests.length === 0 ? (
          <div className="text-gray-500 dark:text-gray-400 text-center py-8">Нет заявок</div>
        ) : (
          <>
            <div className="md:hidden p-2 space-y-2">
              {filteredMyRequests.map((request) => {
                const metrics = calculateMetrics(request);
                const remaining = request.amount - (request.payed_amount || 0) - (request.payed_amount_counterparty || 0);
                const requestMatches = matchedRequests.get(request.id) || [];
                const hasMyInProgressMatch = requestMatches.some((m) => m.status === 'in_progress' && m.counterparty_id === currentUser?.id);
                const hasTeamInProgressMatch = isResponsible && requestMatches.some((m) => m.status === 'in_progress' && m.counterparty_id != null && teamMemberIds.has(m.counterparty_id));
                const hasRelevantInProgressMatch = hasMyInProgressMatch || hasTeamInProgressMatch;
                const shouldBlur = shouldMaskRequestRequisites(
                  request,
                  requestMatches,
                  currentUser?.id,
                  isResponsible,
                  teamMemberIds,
                );
                const isExpanded = expandedRequests.has(request.id);
                const isActiveForActions = request.status === 'work' || request.status === 'in_progress';
                return (
                  <div key={`mobile-my-${request.id}`} className="rounded-xl border border-gray-200 dark:border-gray-700/60 p-2.5 bg-white/80 dark:bg-white/5">
                    <div className="flex items-start justify-between gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(request.order_id);
                            emitToast(`ID ${request.order_id} скопирован`, 'success');
                          } catch {
                            emitToast('Ошибка при копировании', 'error');
                          }
                        }}
                        className="text-xs font-semibold text-blue-600 dark:text-blue-300 underline truncate"
                      >
                        #{maskOrderId(request.order_id)}
                      </button>
                      <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium ${getStatusColor(request.status)}`}>
                        {getStatusText(request.status)}
                      </span>
                    </div>
                    <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {formatNumber(request.amount)} ₽
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-300">
                      Осталось: {formatNumber(remaining)} ₽ · Оплачено: {formatNumber(request.payed_amount || 0)} ₽
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      {formatBankShort(request.bank_name) || '—'} · {isC2CMethod(request.direction_code) ? 'C2C' : (isSBPMethod(request.direction_code) ? 'СБП' : '—')}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => {
                          const next = new Set(expandedRequests);
                          if (next.has(request.id)) next.delete(request.id);
                          else next.add(request.id);
                          setExpandedRequests(next);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15 text-xs"
                      >
                        {isExpanded ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {isExpanded ? 'Скрыть' : 'Детали'}
                      </button>
                      {isActiveForActions && (remaining > 0 || hasRelevantInProgressMatch) && (
                        <button
                          onClick={() => openPayoutWidgetForRequest(request, metrics.remaining)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-colors text-xs ring-1 ring-emerald-200/60 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/30 dark:hover:bg-emerald-500/25"
                        >
                          <Coins className="w-3.5 h-3.5" />
                          Выплаты
                        </button>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700/60 bg-gray-50/70 dark:bg-gray-900/40 p-2 space-y-1.5 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500 dark:text-gray-400">ID</span>
                          <span className="font-mono text-gray-900 dark:text-gray-100">#{request.id}</span>
                        </div>
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-gray-500 dark:text-gray-400">Реквизиты</span>
                          <span className="flex min-w-0 flex-1 items-center justify-end gap-2">
                            {!shouldBlur && (
                              <RequisiteTypeSwitch
                                request={request}
                                selectedType={getSelectedRequisiteType(request, requisitesViewByRequestId[request.id])}
                                onSelect={(type) => setRequisitesViewByRequestId((prev) => ({ ...prev, [request.id]: type }))}
                              />
                            )}
                            <span
                              className={`font-medium truncate ${shouldBlur ? 'blur-sm select-none text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}
                            >
                              {shouldBlur ? MASKED_REQUISITE_TEXT : getDisplayRequisite(request, requisitesViewByRequestId[request.id])}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500 dark:text-gray-400">Держатель</span>
                          <span
                            className={`font-medium truncate ${shouldBlur ? 'blur-sm select-none text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}
                          >
                            {shouldBlur ? MASKED_HOLDER_TEXT : (request.card_holder || '—')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-500 dark:text-gray-400">Создано</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(request.created_at)} {formatTime(request.created_at)}</span>
                        </div>
                        {request.deadline_at ? (
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-500 dark:text-gray-400">Дедлайн</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {formatDate(request.deadline_at)} {formatTime(request.deadline_at)}
                            </span>
                          </div>
                        ) : null}
                        {request.deadline_at && (request.status === 'work' || request.status === 'in_progress') ? (
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-500 dark:text-gray-400">Осталось</span>
                            <span
                              className={`font-semibold tabular-nums ${
                                new Date(request.deadline_at).getTime() <= currentTime.getTime()
                                  ? 'text-rose-600 dark:text-rose-400'
                                  : 'text-gray-900 dark:text-gray-100'
                              }`}
                            >
                              {formatDeadlineRemaining(request.deadline_at, currentTime)}
                            </span>
                          </div>
                        ) : null}
                        {shouldBlur ? (
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 pt-0.5">
                            Реквизиты доступны при активном мэтче «В процессе»
                          </p>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table
                ref={bodyTableRef}
                className="w-full"
                style={
                  colWidths.length === 11
                    ? {
                        tableLayout: 'fixed',
                        width: colWidths.reduce((a, b) => a + b, 0),
                        minWidth: colWidths.reduce((a, b) => a + b, 0),
                      }
                    : undefined
                }
              >
                {colWidths.length === 11 && (
                  <colgroup>
                    {colWidths.map((w, i) => (
                      <col key={i} style={{ width: w, minWidth: w }} />
                    ))}
                  </colgroup>
                )}
                <thead className="sticky top-0 z-10 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-indigo-950 dark:via-purple-950 dark:to-indigo-950">
                  <tr className="divide-x divide-gray-200/20 dark:divide-white/5 [&>th]:overflow-hidden">
                    <th className="relative rounded-tl-2xl px-1 py-3 text-center text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none">Действия<span onMouseDown={(e) => startHeaderResize(0, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" /></th>
                    <th className="relative px-2 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none">WT ID<span onMouseDown={(e) => startHeaderResize(1, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" /></th>
                    <th className="relative px-2 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none">Сумма<span onMouseDown={(e) => startHeaderResize(2, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" /></th>
                    <th className="relative px-2 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider max-w-[100px] select-none">Статус<span onMouseDown={(e) => startHeaderResize(3, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" /></th>
                    <th className="relative px-1 py-3 text-center text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider w-[56px] max-w-[56px] select-none">
                      <div className="flex flex-col items-center">
                        <span className="text-[9px]">Мэтчи</span>
                        <div className="w-3 h-px bg-gray-400 dark:bg-gray-500 my-0.5" />
                        <span className="text-[6px] font-normal opacity-75 lowercase">тек. / макс.</span>
                      </div>
                      <span onMouseDown={(e) => startHeaderResize(4, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" />
                    </th>
                    <th className="relative px-5 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none">
                      <div className="flex flex-col">
                        <span className="text-[9px]">Осталось</span>
                        <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                        <span className="text-[9px]">Выдано</span>
                        <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                        <span className="text-[9px]">Оплачено</span>
                      </div>
                      <span onMouseDown={(e) => startHeaderResize(5, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" />
                    </th>
                    <th className="relative px-5 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none">Реквизиты<span onMouseDown={(e) => startHeaderResize(6, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" /></th>
                    <th className="relative px-5 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap select-none" title="Держатель">Держ.<span onMouseDown={(e) => startHeaderResize(7, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" /></th>
                    <th className="relative px-5 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none">
                      <div className="flex flex-col">
                        <span className="text-[9px]">Банк</span>
                        <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                        <span className="text-[9px]">Метод</span>
                      </div>
                      <span onMouseDown={(e) => startHeaderResize(8, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" />
                    </th>
                    <th className="relative px-5 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none">
                      <div className="flex flex-col">
                        <span className="text-[9px]">Курс</span>
                        <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                        <span className="text-[9px]">Получили</span>
                      </div>
                      <span onMouseDown={(e) => startHeaderResize(9, e)} className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-20" />
                    </th>
                    <th className="relative rounded-tr-2xl px-5 py-3 text-left text-[10px] font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider select-none">Время<span onMouseDown={(e) => startHeaderResize(10, e)} className="absolute top-0 right-0 h-full w-4 cursor-col-resize z-20 after:content-[''] after:absolute after:right-1 after:top-1/2 after:-translate-y-1/2 after:h-4 after:w-px after:bg-gray-400/70" /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100/60 dark:divide-gray-800">
                  {filteredMyRequests.map((request, idx) => {
                    const metrics = calculateMetrics(request);
                    const remaining = request.amount - (request.payed_amount || 0) - (request.payed_amount_counterparty || 0);
                    const isActiveForActions = request.status === 'work' || request.status === 'in_progress';
                    const requestMatches = matchedRequests.get(request.id) || [];
                    const hasMyInProgressMatch = requestMatches.some((m) => m.status === 'in_progress' && m.counterparty_id === currentUser?.id);
                    const hasTeamInProgressMatch = isResponsible && requestMatches.some((m) => m.status === 'in_progress' && m.counterparty_id != null && teamMemberIds.has(m.counterparty_id));
                    const hasRelevantInProgressMatch = hasMyInProgressMatch || hasTeamInProgressMatch;
                    const shouldBlur = shouldMaskRequestRequisites(
                      request,
                      requestMatches,
                      currentUser?.id,
                      isResponsible,
                      teamMemberIds,
                    );
                    const isExpanded = expandedRequests.has(request.id);
                    const isTail = isTailRequest(request);
                    const statusGradient = getStatusRowGradient(request.status || 'open');
                    return (
                      <React.Fragment key={request.id}>
                        <tr
                          className={`transition-colors divide-x divide-gray-200/20 dark:divide-white/5 [&>td]:overflow-hidden ${
                            payoutWidgetOpen === request.id
                              ? 'bg-emerald-50 border-l-4 border-emerald-400 dark:bg-emerald-500/10 dark:border-emerald-400/50'
                              : isTail
                                ? 'bg-sky-50/70 border-l-4 border-sky-400 hover:bg-sky-50/90 dark:bg-sky-500/10 dark:border-sky-400/50'
                                : `${statusGradient} hover:opacity-80`
                          }`}
                        >
                          <td className="pl-1 pr-2 py-3 whitespace-nowrap text-xs">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  const next = new Set(expandedRequests);
                                  if (next.has(request.id)) next.delete(request.id);
                                  else next.add(request.id);
                                  setExpandedRequests(next);
                                }}
                                className="flex items-center justify-center px-1 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors self-stretch ring-1 ring-slate-200/60 dark:bg-white/10 dark:text-slate-200 dark:ring-white/15 dark:hover:bg-white/15"
                                title={isExpanded ? 'Скрыть детали' : 'Показать детали'}
                              >
                                {isExpanded ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                              <div className="flex flex-col gap-1">
                                {/* Единая кнопка «Выплаты» для work/in_progress: свои мэтчи (или участников у ответственного) + плюсик в виджете */}
                                {isActiveForActions && (remaining > 0 || hasRelevantInProgressMatch) && (
                                  <button
                                    onClick={() => openPayoutWidgetForRequest(request, metrics.remaining)}
                                    className="px-2 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-colors text-[9px] font-medium flex items-center gap-1 ring-1 ring-emerald-200/60 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/30 dark:hover:bg-emerald-500/25"
                                    title="Выплаты"
                                  >
                                    <Coins className="w-3 h-3" />
                                    <span>Выплаты</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100">
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleIssuedRequest(request.id);
                                }}
                                className={`shrink-0 p-1 rounded transition-colors ${
                                  issuedRequestIds.has(request.id)
                                    ? 'bg-amber-400/80 text-amber-900 hover:bg-amber-500/90 dark:bg-amber-500/60 dark:text-amber-950 dark:hover:bg-amber-500/80'
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:bg-white/10 dark:text-gray-500 dark:hover:bg-white/20 dark:hover:text-gray-300'
                                }`}
                                title={issuedRequestIds.has(request.id) ? 'Снять маркер «выдал»' : 'Отметить: выдал эту заявку'}
                              >
                                <Flag className="w-3.5 h-3.5" fill={issuedRequestIds.has(request.id) ? 'currentColor' : 'none'} />
                              </button>
                              <span
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(request.order_id);
                                    emitToast(`ID ${request.order_id} скопирован`, 'success');
                                  } catch {
                                    emitToast('Ошибка при копировании', 'error');
                                  }
                                }}
                                className={`underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-all ${
                                  issuedRequestIds.has(request.id)
                                    ? 'text-amber-600 dark:text-amber-400 font-semibold shadow-[0_0_8px_rgba(245,158,11,0.4)] dark:shadow-[0_0_10px_rgba(251,191,36,0.5)] rounded px-0.5'
                                    : ''
                                }`}
                                title={`${request.order_id} — кликните, чтобы скопировать`}
                              >
                                #{maskOrderId(request.order_id)}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100">
                            <div className="flex items-center gap-1.5">
                              <span>{formatNumber(request.amount)} ₽</span>
                              <button
                                type="button"
                                className="p-0.5 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-700 dark:hover:bg-white/10 dark:text-blue-300"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(buildRequestClipboardText(request));
                                    emitToast('Данные заявки скопированы', 'success');
                                  } catch {
                                    emitToast('Ошибка копирования', 'error');
                                  }
                                }}
                                title="Копировать данные заявки"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                          <td className="px-2 py-3 text-xs text-gray-900 dark:text-gray-100 max-w-[100px] overflow-hidden">
                            <div className="flex flex-col min-w-0">
                              <span className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-semibold ${getStatusColor(request.status)} text-left truncate`} title={getStatusText(request.status)}>
                                {getStatusText(request.status)}
                              </span>
                            </div>
                          </td>
                          <td className="px-1 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 text-center w-[56px] max-w-[56px]">
                            <div className="flex flex-col items-center">
                              <span className="text-[10px] text-sky-700 dark:text-sky-200 font-medium">{metrics.totalUsedMatches ?? metrics.usedMatches ?? 0}</span>
                              <div className="w-3 h-px bg-gray-400 dark:bg-gray-500 my-0.5" />
                              <span className="text-[10px] text-gray-600 dark:text-gray-300">{request.max_matches || 0}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100">
                            <div className="flex flex-col">
                              <span>{formatNumber(metrics.remaining)} ₽</span>
                              <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                              <span>{formatNumber(metrics.issued)} ₽</span>
                              <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                              <span>{formatNumber(metrics.paid)} ₽</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 font-mono relative">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const hasPhone = !!getRequestPhoneValue(request);
                                const hasCard = !!getRequestCardValue(request);
                                const selectedType = getSelectedRequisiteType(request, requisitesViewByRequestId[request.id]);
                                return (
                                  <>
                                    {hasPhone && (
                                      <button
                                        type="button"
                                        title="СБП"
                                        className={`p-0.5 rounded transition-colors ${
                                          selectedType === 'phone'
                                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300'
                                            : 'text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setRequisitesViewByRequestId((prev) => ({ ...prev, [request.id]: 'phone' }));
                                        }}
                                      >
                                        <Phone className="h-4 w-4" />
                                      </button>
                                    )}
                                    {hasCard && (
                                      <button
                                        type="button"
                                        title="Карта"
                                        className={`p-0.5 rounded transition-colors ${
                                          selectedType === 'card'
                                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300'
                                            : 'text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setRequisitesViewByRequestId((prev) => ({ ...prev, [request.id]: 'card' }));
                                        }}
                                      >
                                        <CreditCard className="h-4 w-4" />
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                              <span
                                className={`relative z-10 text-gray-900 dark:text-gray-50 ${shouldBlur ? 'blur-sm select-none' : 'underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400'} transition-colors`}
                                onClick={async () => {
                                  const selectedType = getSelectedRequisiteType(request, requisitesViewByRequestId[request.id]);
                                  const value = selectedType === 'phone' ? getRequestPhoneValue(request) : getRequestCardValue(request);
                                  if (!shouldBlur && value) {
                                    try {
                                      const toCopy = selectedType === 'phone' ? value : getDisplayRequisite(request, selectedType);
                                      await navigator.clipboard.writeText(toCopy === '—' ? '' : toCopy);
                                      emitToast('Реквизиты скопированы в буфер обмена', 'success');
                                    } catch {
                                      emitToast('Ошибка при копировании', 'error');
                                    }
                                  }
                                }}
                                title={shouldBlur ? '' : 'Кликните для копирования'}
                              >
                                {getDisplayRequisite(request, requisitesViewByRequestId[request.id])}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 relative">
                            {isTail && (
                              <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-0 overflow-visible">
                                <span className="text-blue-700/20 dark:text-blue-300/10 font-extrabold tracking-[0.12em] blur-[1.8px] text-[24px] leading-none select-none whitespace-nowrap">ХВОСТ</span>
                              </div>
                            )}
                            <span className={`relative z-10 ${shouldBlur ? 'blur-sm select-none' : ''}`}>{request.card_holder || '—'}</span>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100">
                            <div className="flex flex-col">
                              <span>{request.bank_name || '—'}</span>
                              <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                              <span className="text-gray-500 dark:text-gray-400">
                                {request.direction_code === 'tbank_c2c' && 'T-Bank C2C'}
                                {request.direction_code === 'tbank_sbp' && 'T-Bank SBP'}
                                {request.direction_code === 'interbank_c2c' && 'Interbank C2C'}
                                {request.direction_code === 'interbank_sbp' && 'Interbank SBP'}
                                {!request.direction_code && '—'}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100">
                            <div className="flex flex-col">
                              <span>{formatNumber(metrics.calculatedRate)}</span>
                              <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                              <span>{formatNumber(metrics.usdtReceived)}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 w-[1%] min-w-[10.5rem]">
                            <div className="flex flex-col min-w-0">
                              <span className="tabular-nums">{formatDate(request.created_at)} {formatTime(request.created_at)}</span>
                              <div className="w-full h-px bg-gray-300/30 dark:bg-gray-500/40 my-0.5" />
                              <div className="flex items-center gap-1 min-w-0">
                                {request.deadline_at && (request.status === 'work' || request.status === 'in_progress') ? (
                                  <>
                                    <span className="text-gray-500 dark:text-gray-400 shrink-0">Осталось:</span>
                                    <span
                                      className={`tabular-nums min-w-[7rem] inline-block ${
                                        new Date(request.deadline_at).getTime() <= currentTime.getTime()
                                          ? 'text-rose-600 dark:text-rose-400 font-medium'
                                          : ''
                                      }`}
                                    >
                                      {formatDeadlineRemaining(request.deadline_at, currentTime)}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-gray-500 dark:text-gray-400 shrink-0">Создание:</span>
                                    <span className="tabular-nums min-w-[7rem] inline-block">
                                      {(() => {
                                        const wt = calculateWorkTimeFull(
                                          request.created_at || '',
                                          request.work_started_at,
                                          request.work_finished_at
                                        );
                                        if (typeof wt === 'string') return wt;
                                        return <span className={wt.color}>{wt.text}</span>;
                                      })()}
                                    </span>
                                  </>
                                )}
                              </div>
                              {(() => {
                                const myMatch = getMyOrTeamInProgressMatch(request.id);
                                if (!myMatch?.created_at) return null;
                                const inWork = formatDurationFrom(myMatch.created_at);
                                return (
                                  <div className="flex items-center gap-1 mt-0.5 min-w-0">
                                    <span className="text-gray-500 dark:text-gray-400 shrink-0">В работе:</span>
                                    <span className={`tabular-nums min-w-[7rem] inline-block ${typeof inWork === 'string' ? '' : inWork.color}`}>
                                      {typeof inWork === 'string' ? inWork : inWork.text}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                        {/* Строка со стрелкой: выдвижная таблица мэтчей — показываем во всех статусах заявки (как у трейдера) */}
                        <tr className="bg-transparent border-0" style={{ height: 0 }}>
                            <td colSpan={11} className="p-0 border-0 bg-transparent align-top overflow-visible relative" style={{ height: 0, padding: 0, lineHeight: 0, fontSize: 0 }}>
                              <div className="absolute left-1/2 top-0 -translate-x-1/2 z-20">
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const isCurrentlyExpanded = expandedMatchRows.has(request.id);
                                    setExpandedMatchRows((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(request.id)) next.delete(request.id);
                                      else next.add(request.id);
                                      return next;
                                    });
                                    if (!isCurrentlyExpanded) {
                                      setLoadingMatchesRequestId(request.id);
                                      try {
                                        const list = (await cpApi.getAirexRequestMatches(request.id)) as MatchedRequest[];
                                        setMatchedRequests((prev) => {
                                          const next = new Map(prev);
                                          next.set(request.id, list);
                                          return next;
                                        });
                                      } catch {
                                        // ignore
                                      } finally {
                                        setLoadingMatchesRequestId(null);
                                      }
                                    }
                                  }}
                                  className={`flex items-center justify-center w-40 min-w-[10rem] h-4 rounded-b-[1.25rem] border border-t-0 shadow-md transition-all hover:scale-[1.02] ${statusGradient} border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-black/25 hover:bg-white/90 dark:hover:bg-black/40 text-gray-600 dark:text-gray-300`}
                                  title={expandedMatchRows.has(request.id) ? 'Свернуть мэтчи' : 'Развернуть мэтчи'}
                                >
                                  {expandedMatchRows.has(request.id) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        {expandedMatchRows.has(request.id) && (() => {
                          const list = matchedRequests.get(request.id) || [];
                          const isLoading = loadingMatchesRequestId === request.id;
                          const showContent = list.length > 0 || isLoading;
                          const getMatchStatusColor = (s: string): string => {
                            switch ((s || '').toLowerCase()) {
                              case 'closed': case 'confirmed': return 'text-green-700 dark:text-green-400';
                              case 'in_progress': return 'text-blue-700 dark:text-blue-400';
                              case 'canceled': case 'rejected': case 'problem': return 'text-red-700 dark:text-red-400';
                              default: return 'text-gray-700 dark:text-gray-300';
                            }
                          };
                          const getMatchStatusText = (s: string): string => {
                            switch ((s || '').toLowerCase()) {
                              case 'closed': return 'Закрыт';
                              case 'confirmed': return 'Подтвержден';
                              case 'in_progress': return 'В процессе';
                              case 'canceled': return 'Отменен';
                              case 'open': return 'Открыт';
                              default: return s || '—';
                            }
                          };
                          const formatDateTime = (d: string | null | undefined): string => {
                            if (!d) return '—';
                            try {
                              const date = new Date(d);
                              return isNaN(date.getTime()) ? '—' : formatInTimeZone(date, 'Europe/Moscow', 'dd.MM.yyyy HH:mm');
                            } catch { return '—'; }
                          };
                          /** Время в работе мэтча: in_progress — от created_at до сейчас; closed/canceled — от created_at до updated_at */
                          const formatWorkTimeSinceCreated = (createdAt: string | null, updatedAt: string | null, status: string): string => {
                            if (!createdAt) return '—';
                            const s = (status || '').toLowerCase();
                            try {
                              const start = new Date(createdAt);
                              if (isNaN(start.getTime())) return '—';
                              const end = s === 'in_progress' ? new Date() : (updatedAt ? new Date(updatedAt) : null);
                              if (!end || isNaN(end.getTime())) return '—';
                              const minutes = differenceInMinutes(end, start);
                              if (minutes < 0) return '—';
                              if (minutes < 60) return `${minutes} мин`;
                              const h = Math.floor(minutes / 60);
                              const m = minutes % 60;
                              return m ? `${h} ч ${m} мин` : `${h} ч`;
                            } catch { return '—'; }
                          };
                          const rawList = isResponsible ? list : list.filter((m) => m.counterparty_id === currentUser?.id);
                          /** Порядок статуса: в процессе → закрыто → отменена */
                          const statusOrder = (s: string) => ((s || '').toLowerCase() === 'in_progress' ? 1 : (s || '').toLowerCase() === 'closed' || (s || '').toLowerCase() === 'confirmed' ? 2 : 3);
                          /** Сначала свои/команды (в процессе → закрыт → отменён), потом чужие в том же порядке по статусу */
                          const displayList = [...rawList].sort((a, b) => {
                            const isMyOrTeamA = currentUser && (a.counterparty_id === currentUser.id || (isResponsible && teamMemberIds.has(a.counterparty_id ?? 0)));
                            const isMyOrTeamB = currentUser && (b.counterparty_id === currentUser.id || (isResponsible && teamMemberIds.has(b.counterparty_id ?? 0)));
                            if (isMyOrTeamA !== isMyOrTeamB) return isMyOrTeamA ? -1 : 1;
                            return statusOrder(a.status) - statusOrder(b.status) || (a.id - b.id);
                          });
                          return showContent && (
                            <tr className="bg-transparent border-0">
                              <td colSpan={11} className="p-0 align-top border-0 bg-transparent">
                                <style>{`
                                  @keyframes matchesSlideDown {
                                    from { opacity: 0; max-height: 0; transform: translateY(-8px); }
                                    to { opacity: 1; max-height: 800px; transform: translateY(0); }
                                  }
                                  .matches-panel-enter { animation: matchesSlideDown 280ms cubic-bezier(0.22, 1, 0.36, 1) forwards; }
                                `}</style>
                                <div className={`matches-panel-enter overflow-hidden ${statusGradient} rounded-b-xl border border-t-0 border-gray-200/80 dark:border-white/10 shadow-lg`}>
                                  <div className="px-4 pb-2 pt-1">
                                    {isLoading ? (
                                      <div className="py-8 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Загрузка мэтчей…</span>
                                      </div>
                                    ) : (
                                      <div className="overflow-x-auto">
                                        <table className="w-full border-collapse">
                                          <thead>
                                            <tr className="text-[10px] text-gray-600 uppercase tracking-wider dark:text-gray-400 divide-x divide-gray-200/30 dark:divide-white/10 bg-gray-100/80 dark:bg-gray-800/60">
                                              <th className="px-3 py-2 text-left">WT</th>
                                              <th className="px-3 py-2 text-left">Сумма</th>
                                              <th className="px-3 py-2 text-left">Курс</th>
                                              <th className="px-3 py-2 text-left">Получили</th>
                                              <th className="px-3 py-2 text-left">Инициатор</th>
                                              <th className="px-3 py-2 text-left">Статус</th>
                                              <th className="px-3 py-2 text-left">Создан</th>
                                              <th className="px-3 py-2 text-left">В работе</th>
                                              <th className="px-3 py-2 text-left">Чек</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {displayList.map((m) => {
                                              const isMyOrTeam = currentUser && (m.counterparty_id === currentUser.id || (isResponsible && teamMemberIds.has(m.counterparty_id ?? 0)));
                                              return (
                                                <tr key={m.id} className="text-[11px] text-gray-900 dark:text-gray-100 hover:bg-white/60 dark:hover:bg-white/5">
                                                  <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-300">{m.wt_request_id ?? '—'}</td>
                                                  <td className="px-3 py-2 font-semibold">{formatNumber(m.amount)} ₽</td>
                                                  <td className="px-3 py-2">{isMyOrTeam ? <span className="text-blue-700 dark:text-blue-300 font-semibold">{formatNumber(m.exchange_rate)}</span> : '—'}</td>
                                                  <td className="px-3 py-2">{isMyOrTeam ? <span className="text-purple-700 dark:text-purple-300 font-semibold">{formatNumber(m.amount_usd)}</span> : '—'}</td>
                                                  <td className={`px-3 py-2 ${!isMyOrTeam ? 'bg-red-100/50 dark:bg-red-900/20 text-red-800 dark:text-red-200' : 'text-gray-700 dark:text-gray-300'}`}>{isMyOrTeam ? (m.initiator_name || '—') : '—'}</td>
                                                  <td className={`px-3 py-2 font-semibold ${getMatchStatusColor(m.status)}`}>{getMatchStatusText(m.status)}</td>
                                                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{formatDateTime(m.created_at)}</td>
                                                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{isMyOrTeam ? formatWorkTimeSinceCreated(m.created_at, m.updated_at, m.status) : '—'}</td>
                                                  <td className="px-3 py-2">
                                                    {isMyOrTeam && m.wt_request_id ? (
                                                      <button
                                                        type="button"
                                                        className="px-2.5 py-1 rounded-lg text-[11px] font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-200 dark:hover:bg-blue-800/60 transition-colors border border-blue-200/50 dark:border-blue-700/50"
                                                        onClick={() => setReceiptWidget({ wtRequestId: m.wt_request_id!, receiptFilePath: m.receipt_file_path })}
                                                      >
                                                        Чек
                                                      </button>
                                                    ) : <span className="text-gray-400 dark:text-gray-500">—</span>}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                        {isExpanded && (
                          <tr key={`${request.id}-exp`} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/40 dark:bg-gray-900/40'}>
                            <td colSpan={11} className="pl-1 pr-5 pb-4">
                              <style>{`
                                @keyframes airexSlideInRow {
                                  from { opacity: 0; transform: translateX(24px) translateY(-6px); }
                                  to { opacity: 1; transform: translateX(0) translateY(0); }
                                }
                              `}</style>
                              <div
                                className="airex-details-widget mt-2 rounded-2xl border border-purple-200 bg-gradient-to-br from-white to-purple-50/40 shadow-sm overflow-hidden dark:border-gray-700/60 dark:from-gray-900 dark:to-gray-800"
                                style={{ animation: 'airexSlideInRow 220ms cubic-bezier(0.16, 1, 0.3, 1)', willChange: 'transform, opacity' }}
                              >
                                <div className="px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white flex items-center gap-3">
                                  <div className="flex gap-2 flex-shrink-0">
                                    <span className="px-2 py-1 rounded-md text-[10px] font-semibold bg-white/25 text-white dark:bg-white/20 dark:text-white">Детали</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-center gap-4">
                                      <div className="min-w-0 text-center">
                                        <div className="text-[10px] uppercase tracking-wider text-purple-100/90">Заявка</div>
                                        <div
                                          className="text-lg font-extrabold leading-tight cursor-pointer hover:text-purple-100 transition-colors truncate"
                                          onClick={async () => {
                                            try {
                                              await navigator.clipboard.writeText(request.order_id);
                                              emitToast(`ID ${request.order_id} скопирован`, 'success');
                                            } catch {
                                              emitToast('Ошибка при копировании', 'error');
                                            }
                                          }}
                                          title="Копировать ID"
                                        >
                                          {request.order_id}
                                        </div>
                                        <div className="mt-1 flex flex-wrap items-center justify-center gap-1">
                                          <span className="px-2 py-0.5 rounded-full text-[10px] bg-white/10 text-purple-50">ID: <span className="font-semibold">#{request.id}</span></span>
                                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/15 ${
                                            request.status === 'closed' ? 'text-green-200' :
                                            request.status === 'work' || request.status === 'in_progress' ? 'text-yellow-200' :
                                            request.status === 'problem' || request.status === 'returned' ? 'text-red-200' : 'text-purple-50'
                                          }`}>
                                            {getStatusText(request.status)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => {
                                      const next = new Set(expandedRequests);
                                      next.delete(request.id);
                                      setExpandedRequests(next);
                                    }}
                                    className="p-1 rounded hover:bg-white/15 transition-colors flex-shrink-0"
                                    title="Скрыть"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="p-4">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                      {(() => {
                                        const detailsMetrics = calculateMetricsForDetails(request);
                                        return (
                                      <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-200 dark:bg-gray-900/60 dark:border-gray-700/60">
                                        <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-100 mb-2 flex items-center gap-2">
                                          <div className="w-2 h-2 bg-blue-500 rounded-full" />Основное{isResponsible && <span className="text-[9px] font-normal text-gray-500 dark:text-gray-400">(команда)</span>}
                                        </div>
                                        <div className="space-y-2 text-[11px]">
                                          <div className="flex justify-between gap-2">
                                            <span className="text-gray-500 dark:text-gray-400">Сумма</span>
                                            <span className="font-semibold text-green-700 dark:text-green-400 flex items-center gap-1.5">
                                              {formatNumber(request.amount)} ₽
                                              <button
                                                type="button"
                                                className="p-0.5 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-700 dark:hover:bg-white/10 dark:text-blue-300"
                                                onClick={async () => {
                                                  try {
                                                    await navigator.clipboard.writeText(buildRequestClipboardText(request));
                                                    emitToast('Данные заявки скопированы', 'success');
                                                  } catch {
                                                    emitToast('Ошибка копирования', 'error');
                                                  }
                                                }}
                                                title="Копировать данные заявки"
                                              >
                                                <Copy className="w-3.5 h-3.5" />
                                              </button>
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-2"><span className="text-gray-500 dark:text-gray-400">Оплачено</span><span className="font-semibold text-gray-900 dark:text-gray-100">{formatNumber(detailsMetrics.paid)} ₽</span></div>
                                          <div className="flex justify-between gap-2"><span className="text-gray-500 dark:text-gray-400">Промеж. (выдано)</span><span className="font-semibold text-gray-900 dark:text-gray-100">{formatNumber(detailsMetrics.issued)} ₽</span></div>
                                          <div className="flex justify-between gap-2"><span className="text-gray-500 dark:text-gray-400">Остаток</span><span className="font-semibold text-gray-900 dark:text-gray-100">{formatNumber(detailsMetrics.remaining)} ₽</span></div>
                                        </div>
                                      </div>
                                        );
                                      })()}
                                      <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-200 dark:bg-gray-900/60 dark:border-gray-700/60">
                                        <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-100 mb-2 flex items-center gap-2">
                                          <div className="w-2 h-2 bg-purple-500 rounded-full" />Реквизиты
                                        </div>
                                        <div className="space-y-2 text-[11px]">
                                          <div className="flex justify-between items-start gap-2">
                                            <span className="text-gray-500 dark:text-gray-400">Карта/мт</span>
                                            <span className="flex min-w-0 flex-1 items-center justify-end gap-2">
                                              {!shouldBlur && (
                                                <RequisiteTypeSwitch
                                                  request={request}
                                                  selectedType={getSelectedRequisiteType(request, requisitesViewByRequestId[request.id])}
                                                  onSelect={(type) => setRequisitesViewByRequestId((prev) => ({ ...prev, [request.id]: type }))}
                                                />
                                              )}
                                              <span
                                                className={`font-mono truncate ${shouldBlur ? 'blur-sm select-none text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100 underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400'}`}
                                                onClick={async () => {
                                                  if (shouldBlur) return;
                                                  try {
                                                    const t = getSelectedRequisiteType(request, requisitesViewByRequestId[request.id]);
                                                    const toCopy = t === 'phone' ? getRequestPhoneValue(request) : getDisplayRequisite(request, t);
                                                    await navigator.clipboard.writeText(toCopy === '—' ? '' : toCopy);
                                                    emitToast('Карта скопирована', 'success');
                                                  } catch {
                                                    emitToast('Ошибка копирования', 'error');
                                                  }
                                                }}
                                                title={shouldBlur ? '' : 'Клик для копирования'}
                                              >
                                                {shouldBlur ? MASKED_REQUISITE_TEXT : getDisplayRequisite(request, requisitesViewByRequestId[request.id])}
                                              </span>
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center gap-2">
                                            <span className="text-gray-500 dark:text-gray-400">Держатель</span>
                                            <span
                                              className={`font-medium ${shouldBlur ? 'blur-sm select-none text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100 underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400'}`}
                                              onClick={async () => {
                                                if (shouldBlur) return;
                                                try {
                                                  await navigator.clipboard.writeText(request.card_holder || '');
                                                  emitToast('Держатель скопирован', 'success');
                                                } catch {
                                                  emitToast('Ошибка копирования', 'error');
                                                }
                                              }}
                                              title={shouldBlur ? '' : 'Клик для копирования'}
                                            >
                                              {shouldBlur ? MASKED_HOLDER_TEXT : (request.card_holder || '—')}
                                            </span>
                                          </div>
                                          {shouldBlur ? (
                                            <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                              Реквизиты доступны при активном мэтче «В процессе»
                                            </p>
                                          ) : null}
                                          <div className="flex justify-between items-center gap-2">
                                            <span className="text-gray-500 dark:text-gray-400">Банк</span>
                                            <span
                                              className="font-medium text-gray-900 dark:text-gray-100 underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                                              onClick={async () => { try { await navigator.clipboard.writeText(request.bank_name || ''); emitToast('Банк скопирован', 'success'); } catch { emitToast('Ошибка копирования', 'error'); } }}
                                              title="Клик для копирования"
                                            >
                                              {request.bank_name || '—'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center gap-2"><span className="text-gray-500 dark:text-gray-400">Метод</span><span className="font-medium text-gray-900 dark:text-gray-100">
                                            {request.direction_code === 'tbank_c2c' && 'T-Bank C2C'}
                                            {request.direction_code === 'tbank_sbp' && 'T-Bank SBP'}
                                            {request.direction_code === 'interbank_c2c' && 'Interbank C2C'}
                                            {request.direction_code === 'interbank_sbp' && 'Interbank SBP'}
                                            {!request.direction_code && '—'}
                                          </span></div>
                                        </div>
                                      </div>
                                      <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-200 dark:bg-gray-900/60 dark:border-gray-700/60">
                                        <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-100 mb-2 flex items-center gap-2">
                                          <div className="w-2 h-2 bg-orange-500 rounded-full" />Время
                                        </div>
                                        <div className="space-y-2 text-[11px]">
                                          <div className="flex justify-between gap-2"><span className="text-gray-500 dark:text-gray-400">Создано</span><span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(request.created_at)} {formatTime(request.created_at)}</span></div>
                                          {request.deadline_at ? (
                                            <div className="flex justify-between gap-2">
                                              <span className="text-gray-500 dark:text-gray-400">Дедлайн</span>
                                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                                {formatDate(request.deadline_at)} {formatTime(request.deadline_at)}
                                              </span>
                                            </div>
                                          ) : null}
                                          <div className="flex justify-between gap-2"><span className="text-gray-500 dark:text-gray-400">Закрыто</span><span className="font-medium text-gray-900 dark:text-gray-100">{request.work_finished_at ? `${formatDate(request.work_finished_at)} ${formatTime(request.work_finished_at)}` : '—'}</span></div>
                                        </div>
                                      </div>
                                    </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* Пустая строка в конце: место для стрелки последней заявки (стрелка в нулевой по высоте строке уходит вниз) */}
                  <tr className="bg-transparent border-0" style={{ height: '1.5rem' }} aria-hidden="true">
                    <td colSpan={11} className="p-0 border-0 bg-transparent" style={{ height: '1.5rem', padding: 0 }} />
                  </tr>
                </tbody>
              </table>
            </div>
            {myTotalPages > 1 && (
              <div className="flex justify-center items-center gap-2 py-3 px-4 border-t border-gray-200 dark:border-gray-700/60">
                <button
                  onClick={() => setMyRequestsCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={myRequestsCurrentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Назад
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 px-2">
                  {myRequestsCurrentPage} / {myTotalPages}
                </span>
                <button
                  onClick={() => setMyRequestsCurrentPage((p) => Math.min(myTotalPages, p + 1))}
                  disabled={myRequestsCurrentPage === myTotalPages}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Вперед
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {payoutWidgetOpen && (() => {
        const request = myRequests.find((r) => r.id === payoutWidgetOpen);
        if (!request) return null;
        const metrics = calculateMetrics(request);
        const remaining = request.amount - (request.payed_amount || 0) - (request.payed_amount_counterparty || 0);
        const requestMatches = matchedRequests.get(payoutWidgetOpen) || [];
        const existingMatches = payoutWidgetMode === 'payout'
          ? (isResponsible
              ? requestMatches.filter((m) => m.status === 'in_progress' && (m.counterparty_id === currentUser?.id || (m.counterparty_id != null && teamMemberIds.has(m.counterparty_id))))
              : requestMatches.filter((m) => m.status === 'in_progress' && m.counterparty_id === currentUser?.id))
          : [];
        const selectedExistingMatch = selectedMatchId
          ? existingMatches.find((m) => m.id === selectedMatchId) ?? null
          : (existingMatches[0] ?? null);
        const amt = parseFloat(payoutAmount);
        const rate = metrics.calculatedRate;
        const calculatedUsdt = amt > 0 && rate > 0 ? (amt / rate).toFixed(2) : '';
        const canKeepRemainder = payoutWidgetMode === 'payout'
          && !!selectedExistingMatch
          && !Number.isNaN(amt)
          && amt > 0
          && amt < selectedExistingMatch.amount - 0.0001;
        const allowReceiptImagesTeam = team?.allow_receipt_pdf_upload === true;
        const payoutReceiptAccept = allowReceiptImagesTeam ? 'application/pdf,image/png,image/jpeg' : 'application/pdf';
        return (
          <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm z-[200] flex items-center justify-center p-2 md:p-4 md:pl-[280px]">
            <div className="bg-white dark:bg-gray-900 rounded-xl md:rounded-2xl shadow-2xl w-full max-w-5xl h-[95dvh] md:max-h-[90vh] overflow-hidden flex flex-col border border-transparent dark:border-gray-700/60">
              <div className={`relative flex justify-between items-start md:items-center px-3 md:px-6 py-3 md:py-4 border-b border-gray-200 dark:border-gray-700/60 bg-gradient-to-r ${payoutWidgetMode === 'payout' ? 'from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800' : 'from-orange-50 to-amber-50 dark:from-gray-900 dark:to-gray-800'}`}>
                <div className="flex items-center gap-3 shrink-0">
                  <div className={`p-2 rounded-lg ${payoutWidgetMode === 'payout' ? 'bg-green-100 dark:bg-green-900/40' : 'bg-orange-100 dark:bg-orange-900/40'}`}>
                    <Coins className={`w-6 h-6 ${payoutWidgetMode === 'payout' ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{payoutWidgetMode === 'payout' ? 'Создать выплату' : 'Создать выдачу'}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Заявка #{request.order_id}</p>
                  </div>
                </div>
                {isResponsible && payoutWidgetMode === 'payout' && (() => {
                  const selectedMatch = existingMatches.find((m) => m.id === selectedMatchId) ?? existingMatches[0];
                  const isTeamMemberMatch = selectedMatch && selectedMatch.counterparty_id != null && selectedMatch.counterparty_id !== currentUser?.id && teamMemberIds.has(selectedMatch.counterparty_id);
                  return isTeamMemberMatch ? (
                    <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 dark:bg-amber-900/40 border-2 border-amber-400 dark:border-amber-500 text-amber-900 dark:text-amber-100 text-sm font-semibold shadow-[0_0_20px_rgba(245,158,11,0.4)] dark:shadow-[0_0_24px_rgba(245,158,11,0.35)] ring-2 ring-amber-300/50 dark:ring-amber-500/40 animate-pulse pointer-events-none">
                      <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
                      <span>Внимание! Данная выплата — участника вашей команды!</span>
                    </div>
                  ) : null;
                })()}
                <button
                  onClick={() => {
                    if (isCreatingPayout) return;
                    // Не сохраняем состояние «Создать выдачу» без выбора (после нажатия + и закрытия) — при следующем открытии подставим выплату/остаток по данным
                    if (payoutWidgetOpen != null && !(payoutWidgetMode === 'issue' && !selectedMatchId)) {
                      lastPayoutStateByRequestIdRef.current[payoutWidgetOpen] = {
                        mode: payoutWidgetMode,
                        selectedMatchId,
                        amount: payoutAmount,
                      };
                    }
                    setPayoutWidgetOpen(null);
                    setSelectedMatchId(null);
                    setPayoutAmount('');
                    setPayoutReceiptFile(null);
                    // остаток всегда закрепляется автоматически
                  }}
                  disabled={isCreatingPayout}
                  className={`p-2 hover:bg-gray-100 rounded-lg transition-colors dark:hover:bg-white/5 ${isCreatingPayout ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                </button>
              </div>
              {/* Строка мэтчей + плюсик: показываем для work/in_progress при наличии остатка или мэтчей */}
              {(request.status === 'work' || request.status === 'in_progress') && (existingMatches.length > 0 || remaining > 0) && (
                <div className="mx-4 mt-2 mb-3 shrink-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      {existingMatches.map((match) => {
                        const isSelected = selectedMatchId === match.id || (!selectedMatchId && existingMatches[0]?.id === match.id);
                        const isMyOrTeam = currentUser && (match.counterparty_id === currentUser.id || (isResponsible && teamMemberIds.has(match.counterparty_id ?? 0)));
                        return (
                          <div
                            key={match.id}
                            onClick={() => {
                              setPayoutWidgetMode('payout');
                              setSelectedMatchId(match.id);
                              setPayoutAmount(match.amount.toFixed(2));
                              // остаток всегда закрепляется автоматически
                            }}
                            className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-all cursor-pointer shrink-0 ${
                              isSelected
                                ? 'border-blue-500 ring-1 ring-blue-300 bg-blue-50/80 dark:bg-blue-900/20'
                                : isMyOrTeam
                                  ? 'border-green-400 dark:border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 hover:border-green-500 ring-1 ring-green-200/60 dark:ring-green-500/30'
                                  : 'border-blue-200/70 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 hover:border-blue-300 dark:border-blue-800 dark:from-blue-900/10 dark:to-indigo-900/10'
                            }`}
                          >
                            {match.wt_request_id && <span className="font-semibold text-gray-800 dark:text-gray-200">#{match.wt_request_id}</span>}
                            {isMyOrTeam && match.wt_request_id && match.initiator_name && <span className="text-gray-300 dark:text-gray-600">|</span>}
                            {isMyOrTeam && match.initiator_name ? (
                              <span className="font-medium max-w-[80px] truncate text-green-600 dark:text-green-400" title={match.initiator_name}>{match.initiator_name}</span>
                            ) : !isMyOrTeam ? (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            ) : null}
                            <span className="font-semibold text-blue-700 dark:text-blue-300">{formatNumber(match.amount)} ₽</span>
                            <span className="text-indigo-600 dark:text-indigo-400">{match.exchange_rate ? match.exchange_rate.toFixed(2) : '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                    {remaining > 0 && (
                      <button
                        onClick={() => {
                          setPayoutWidgetMode('issue');
                          setSelectedMatchId(null);
                          setPayoutAmount(remaining.toFixed(2));
                          setPayoutReceiptFile(null);
                          // остаток всегда закрепляется автоматически
                        }}
                        className={`flex items-center justify-center w-8 h-8 rounded-lg border-2 shrink-0 transition-all ${
                          payoutWidgetMode === 'issue' && !selectedMatchId
                            ? 'bg-sky-100 border-sky-500 ring-1 ring-sky-300 dark:bg-sky-900/40 dark:border-sky-400'
                            : 'bg-gradient-to-br from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white border-transparent'
                        }`}
                        title="Создать новый мэтч"
                      >
                        <Plus className={`w-4 h-4 ${payoutWidgetMode === 'issue' && !selectedMatchId ? 'text-sky-600 dark:text-sky-400' : 'text-white'}`} />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {/* Подсказка как у трейдеров */}
              {(request.status === 'work' || request.status === 'in_progress') && remaining > 0 && (
                <div className="mx-4 mb-3 shrink-0 bg-amber-50/80 dark:bg-amber-900/20 rounded-xl p-3 border border-amber-200/60 dark:border-amber-700/50">
                  <h4 className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Подсказка
                  </h4>
                  <p className="text-xs text-amber-900/90 dark:text-amber-100/90 leading-relaxed">
                    Через <span className="font-semibold">+</span> справа от активных выплат можно создать новую. Текущую активную выплату можно закрыть с любой суммой (в меньшую или большую сторону) независимо от поля Сумма.
                  </p>
                </div>
              )}
              <div className="flex flex-1 flex-col md:flex-row overflow-hidden">
                <div className="relative w-full md:w-1/2 border-b md:border-b-0 md:border-r border-blue-200/80 p-3 md:p-6 overflow-y-auto bg-gradient-to-b from-blue-50/60 to-indigo-50/30 dark:border-blue-900/40 dark:from-gray-900 dark:to-gray-900/80">
                  <div className="sticky top-0 z-10 mb-3 -mt-1 rounded-lg border border-blue-200/80 bg-blue-50/95 dark:border-blue-800/60 dark:bg-blue-900/30 px-3 py-2 text-[11px] text-blue-800 dark:text-blue-200 backdrop-blur">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <ChevronDown className="w-3.5 h-3.5" />
                      Блок с деталями заявки прокручивается
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700/60">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-100 mb-3 flex items-center gap-2"><div className="w-1 h-4 bg-green-500 rounded" />Основная информация</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-300">WT ID:</span><span className="font-medium text-gray-900 dark:text-gray-100">{request.order_id}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-300">Сумма заявки:</span><span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(request.amount)} ₽</span></div>
                        <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-300">Осталось:</span><span className="font-medium text-green-600 dark:text-green-400">{formatNumber(remaining)} ₽</span></div>
                        <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-300">Выдано:</span><span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(metrics.issued)} ₽</span></div>
                        <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-300">Оплачено:</span><span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(metrics.paid)} ₽</span></div>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700/60">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-100 mb-3 flex items-center gap-2"><div className="w-1 h-4 bg-purple-500 rounded" />Реквизиты</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center gap-4">
                          <span className="text-gray-600 dark:text-gray-300">Банк:</span>
                          <span
                            className="font-medium text-gray-900 dark:text-gray-100 text-right truncate max-w-[60%] underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                            onClick={async () => { try { await navigator.clipboard.writeText(request.bank_name || ''); emitToast('Банк скопирован', 'success'); } catch { emitToast('Ошибка копирования', 'error'); } }}
                            title="Клик для копирования"
                          >
                            {request.bank_name || '—'}
                          </span>
                        </div>
                        <div className="flex justify-between items-start gap-4">
                          <span className="text-gray-600 dark:text-gray-300">Карта:</span>
                          <span className="flex min-w-0 flex-1 items-center justify-end gap-2">
                            <RequisiteTypeSwitch
                              request={request}
                              selectedType={getSelectedRequisiteType(request, requisitesViewByRequestId[request.id])}
                              onSelect={(type) => setRequisitesViewByRequestId((prev) => ({ ...prev, [request.id]: type }))}
                            />
                            <span
                              className="font-mono text-gray-900 dark:text-gray-100 text-right truncate underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                              onClick={async () => { try { const t = getSelectedRequisiteType(request, requisitesViewByRequestId[request.id]); const toCopy = t === 'phone' ? getRequestPhoneValue(request) : getDisplayRequisite(request, t); await navigator.clipboard.writeText(toCopy === '—' ? '' : toCopy); emitToast('Карта скопирована', 'success'); } catch { emitToast('Ошибка копирования', 'error'); } }}
                              title="Клик для копирования"
                            >
                              {getDisplayRequisite(request, requisitesViewByRequestId[request.id])}
                            </span>
                          </span>
                        </div>
                        <div className="flex justify-between items-center gap-4">
                          <span className="text-gray-600 dark:text-gray-300">Держатель:</span>
                          <span
                            className="font-medium text-gray-900 dark:text-gray-100 text-right truncate max-w-[60%] underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                            onClick={async () => { try { await navigator.clipboard.writeText(request.card_holder || ''); emitToast('Держатель скопирован', 'success'); } catch { emitToast('Ошибка копирования', 'error'); } }}
                            title="Клик для копирования"
                          >
                            {request.card_holder || '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="sticky bottom-0 mt-3 -mb-1 rounded-lg border border-blue-200/80 bg-blue-50/95 dark:border-blue-800/60 dark:bg-blue-900/30 px-3 py-1.5 text-[11px] text-blue-800 dark:text-blue-200 backdrop-blur">
                    <div className="flex items-center justify-center gap-1.5 font-medium">
                      <ChevronDown className="w-3.5 h-3.5" />
                      Листайте вниз/вверх для просмотра всех реквизитов
                    </div>
                  </div>
                </div>
                <div className="relative w-full md:w-1/2 p-3 md:p-6 overflow-y-auto bg-gradient-to-b from-emerald-50/55 to-amber-50/30 dark:from-gray-900 dark:to-gray-900/80">
                  <div className="sticky top-0 z-10 mb-3 -mt-1 rounded-lg border border-amber-200/80 bg-amber-50/95 dark:border-amber-800/60 dark:bg-amber-900/30 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200 backdrop-blur">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <ChevronDown className="w-3.5 h-3.5" />
                      Важно: прокрутите вниз до блока "Чек"
                    </div>
                  </div>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Сумма выплаты (₽)</label>
                      <input type="number" step="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} placeholder={remaining.toFixed(2)} className="w-full border-2 rounded-lg px-4 py-3 text-lg font-medium border-gray-300 dark:border-gray-600 focus:border-green-500 focus:ring-2 focus:ring-green-200 dark:focus:ring-green-900/40 dark:bg-gray-900/40 dark:text-gray-100" />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Осталось: {formatNumber(remaining)} ₽</p>
                      {canKeepRemainder && (
                        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-700/40 dark:bg-emerald-900/20 dark:text-emerald-100">
                          Если вы уменьшите сумму выплаты — <span className="font-semibold">остаток автоматически останется за вами</span> для обязательной доплаты.
                        </div>
                      )}
                    </div>
                    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-4 py-3 border border-gray-200 dark:border-gray-700/60">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Курс по градации суммы заявки</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{rate > 0 ? formatNumber(rate) : '—'}</p>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">USDT получите: <span className="font-medium text-gray-900 dark:text-gray-100">{calculatedUsdt ? formatNumber(parseFloat(calculatedUsdt)) : '—'}</span></p>
                    </div>
                    {payoutWidgetMode === 'payout' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                          Чек <span className="text-red-500">(обязательно)</span>
                        </label>
                        <div
                            className="border-2 border-dashed rounded-lg p-6 hover:border-green-400 dark:hover:border-green-500 transition-colors cursor-pointer border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900/30"
                          onClick={() => fileInputRef.current?.click()}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-green-400', 'bg-green-50/50', 'dark:border-green-500', 'dark:bg-green-900/20'); }}
                          onDragLeave={(e) => { e.currentTarget.classList.remove('border-green-400', 'bg-green-50/50', 'dark:border-green-500', 'dark:bg-green-900/20'); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-50/50', 'dark:border-green-500', 'dark:bg-green-900/20');
                            const file = e.dataTransfer.files[0];
                            if (!file) return;
                            if (!payoutReceiptFileAllowed(file, allowReceiptImagesTeam)) {
                              emitToast(
                                allowReceiptImagesTeam
                                  ? 'Допустимы только PDF, PNG или JPEG.'
                                  : 'Для вашей команды разрешён только PDF.',
                                'error'
                              );
                              return;
                            }
                            setPayoutReceiptFile(file);
                          }}
                        >
                          <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-3">
                            {allowReceiptImagesTeam ? 'PDF + изображения' : 'Только PDF'}
                          </p>
                          <input
                            key={payoutReceiptAccept}
                            ref={fileInputRef}
                            type="file"
                            accept={payoutReceiptAccept}
                            className="hidden"
                            onChange={(e) => { const inp = e.target; const f = inp.files?.[0]; if (!f) return; if (!payoutReceiptFileAllowed(f, allowReceiptImagesTeam)) { emitToast(allowReceiptImagesTeam ? 'Допустимы только PDF, PNG или JPEG.' : 'Для вашей команды разрешён только PDF.', 'error'); inp.value = ''; return; } setPayoutReceiptFile(f); }}
                          />
                          {payoutReceiptFile ? (
                            <div className="text-center">
                              <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">✓ {payoutReceiptFile.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Кликните для замены</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Кликните для выбора файла</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">или перетащите сюда</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <button onClick={handleCreatePayout} disabled={isCreatingPayout} className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50 transition-colors">
                        {isCreatingPayout ? 'Отправка...' : 'Отправить'}
                      </button>
                      {currentUser?.can_cancel && payoutWidgetMode === 'payout' && (() => {
                        const m = existingMatches.find((x) => x.id === selectedMatchId) ?? existingMatches[0];
                        if (!m?.wt_request_id) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => setCancelConfirm({ wtRequestId: m.wt_request_id!, airexOrderId: request.order_id })}
                            disabled={isCreatingPayout || isCanceling}
                            className="px-4 py-3 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg disabled:opacity-50 transition-colors dark:bg-red-900/20 dark:text-red-200 dark:hover:bg-red-900/30 flex items-center gap-2"
                          >
                            <X className="w-4 h-4" />
                            Отменить
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="sticky bottom-0 mt-3 -mb-1 rounded-lg border border-amber-200/80 bg-amber-50/95 dark:border-amber-800/60 dark:bg-amber-900/30 px-3 py-1.5 text-[11px] text-amber-800 dark:text-amber-200 backdrop-blur">
                    <div className="flex items-center justify-center gap-1.5 font-medium">
                      <ChevronDown className="w-3.5 h-3.5" />
                      Если не видите поле чека - прокрутите ниже
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <ConfirmTakeWidget
        isOpen={showConfirmTake}
        onClose={() => {
          setShowConfirmTake(false);
          setSelectedTake(null);
          setTakeIntentRub(null);
        }}
        onConfirm={handleTakeConfirm}
        request={selectedTake}
        displayRate={selectedTake ? calculateMetrics(selectedTake).calculatedRate : undefined}
        plannedMatchRub={takeIntentRub}
      />

      <ConfirmCancelWidget
        isOpen={!!cancelConfirm}
        onClose={() => setCancelConfirm(null)}
        onConfirm={handleRejectWTRequest}
        wtRequestId={cancelConfirm?.wtRequestId ?? null}
        airexOrderId={cancelConfirm?.airexOrderId}
      />

      <ReceiptViewWidget
        isOpen={!!receiptWidget}
        onClose={() => setReceiptWidget(null)}
        wtRequestId={receiptWidget?.wtRequestId ?? null}
        receiptFilePath={receiptWidget?.receiptFilePath}
      />
    </div>
  );
}
